import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { MdDownload, MdDescription, MdPeople, MdContentCopy, MdMenuBook, MdUpload, MdRefresh, MdAdd, MdEdit, MdDeleteOutline, MdArrowUpward, MdArrowDownward, MdPersonOff, MdSyncAlt, MdSave, MdCloudUpload, MdTargetArrow } from 'react-icons/md';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import YearPicker from '../../components/YearPicker';
import { createMonthlyTarget, updateMonthlyTarget, deleteMonthlyTarget, downloadMonthlyTargetTemplate, previewMonthlyTargetTemplate, uploadMonthlyTargets } from '../../services/monthlyTargetService';
import {
  listStaffCourseMappings,
  getStaffCourseMappingOptions,
  createStaffCourseMapping,
  updateStaffCourseMapping,
  deleteStaffCourseMapping,
  getStaffCourseMappingReport,
} from '../../services/staffCourseMappingService';
import { getImageURL } from '../../utils/api';

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SummaryLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const SummaryValue = styled.span`
  font-size: 30px;
  font-weight: 700;
  color: #0f172a;
`;

const SummaryHint = styled.span`
  font-size: 12px;
  color: #475569;
`;

const FiltersRow = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #cbd5f5;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 14px;
  background: #ffffff;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    outline: none;
  }
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(120deg, #2563eb, #6366f1);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(79, 70, 229, 0.35);
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Button)`
  background: #edf2ff;
  color: #312e81;
  border: 1px solid #c7d2fe;
  &:hover {
    background: #e0e7ff;
  }
`;

const DangerButton = styled(Button)`
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  &:hover {
    background: #fecaca;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 16px;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  color: #0f172a;
  background: #ffffff;
`;

const Tr = styled.tr`
  &:nth-child(even) ${Td} {
    background: #f8fafc;
  }
`;

const MetaText = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #e0f2fe;
  color: #0369a1;
`;

const StaffInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StaffName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const StaffPosition = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const StatusBadge = styled(Tag)`
  ${({ $status }) => {
    const preset = statusColorMap[$status];
    if (!preset) return '';
    return `background: ${preset.bg}; color: ${preset.color}; border: none;`;
  }}
`;

const CopyButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: #94a3b8;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #1d4ed8;
  }
`;

const ErrorBanner = styled.div`
  border: 1px solid #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #94a3b8;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
`;

const PaginationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled(Button)`
  background: #ffffff;
  border: 1px solid #cbd5f5;
  color: #1d4ed8;
  padding: 8px 12px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageSizeSelect = styled.select`
  border: 1px solid #cbd5f5;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
`;

const ModalSelect = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 14px;
  background: #ffffff;
  color: #0f172a;
  font-weight: 500;
  transition: border-color 0.2s ease;
  
  &:focus {
    border-color: #2563eb;
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &:hover {
    border-color: #94a3b8;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
  z-index: 1200;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 640px;
  background: #ffffff;
  border-radius: 18px;
  padding: 28px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.35);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const StaffModalCard = styled(ModalCard)`
  max-width: 1080px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  color: #94a3b8;
  cursor: pointer;
  &:hover {
    color: #1f2937;
  }
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  max-height: 520px;
  overflow-y: auto;
  padding: 4px;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const CourseGrid = styled(StaffGrid)`
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
`;

const CourseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
  }
`;

const CourseTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.4;
`;

const CourseMeta = styled.span`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
`;

const StaffCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: default;
  
  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
  }
`;

const StaffAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(135deg, #dbeafe, #c7d2fe);
  color: #1e40af;
  font-weight: 700;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
`;

const StaffPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StaffInitials = styled.span`
  letter-spacing: 0.5px;
`;

const StaffDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const StaffNameLine = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StaffMetaLine = styled.span`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const StaffTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const StaffTag = styled(Tag)`
  background: #eef2ff;
  color: #4338ca;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border: 1px solid #e0e7ff;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.4px;
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #dc2626;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '44px',
    borderRadius: '10px',
    borderColor: state.isFocused ? '#2563eb' : '#d0d7ff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37,99,235,0.15)' : 'none',
    fontSize: '14px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1500,
    fontSize: '14px',
  }),
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ─── Assignment list redesign ────────────────────────────── */
const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GhostButton = styled(Button)`
  background: transparent;
  border: 1.5px solid #cbd5e1;
  color: #64748b;
  padding: 10px 16px;
  font-weight: 600;
  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #0f172a;
  }
`;

/* ── Reusable icon action button ──────────────────────────── */
const IconActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => p.$borderColor || '#e2e8f0'};
  background: ${(p) => p.$bg || '#f8fafc'};
  color: ${(p) => p.$color || '#475569'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  &:hover {
    background: ${(p) => p.$hoverBg || '#f1f5f9'};
    border-color: ${(p) => p.$hoverBorder || '#94a3b8'};
    color: ${(p) => p.$hoverColor || '#0f172a'};
    box-shadow: 0 3px 10px ${(p) => p.$shadow || 'rgba(15,23,42,0.08)'};
  }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const IconBubble = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${(p) => p.$bg || '#e0e7ff'};
  color: ${(p) => p.$color || '#4338ca'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* ── Action group label ───────────────────────────────────── */
const ActionGroupLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
`;

const ActionGroupRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const ActionGroupsBar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px 0 4px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
`;

/* ── Monthly Target Actions strip ──────────────────────────── */
const MtActionsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const MtActionTile = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 14px;
  border: 1.5px solid ${(p) => p.$borderColor || '#e2e8f0'};
  background: ${(p) => p.$bg || '#f8fafc'};
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${(p) => p.$shadow || 'rgba(15,23,42,0.10)'};
    border-color: ${(p) => p.$hoverBorder || '#94a3b8'};
  }
  &:active {
    transform: translateY(0);
  }
`;

const MtTileIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${(p) => p.$iconBg || '#e0e7ff'};
  color: ${(p) => p.$iconColor || '#4338ca'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MtTileText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MtTileTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
`;

const MtTileDesc = styled.span`
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
`;

const MtStripLabel = ActionGroupLabel;

const GroupedList = styled.div`
  display: flex;
  flex-direction: column;
`;

const StaffGroupRow = styled.div`
  border-top: 1px solid #e2e8f0;
  &:first-child {
    border-top: none;
  }
`;

const StaffGroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8fafc;
`;

const StaffGroupInitial = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, #dbeafe, #c7d2fe);
  color: #1e40af;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StaffGroupName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const StaffGroupSub = styled.span`
  font-size: 12px;
  color: #94a3b8;
  margin-left: 4px;
`;

const StaffGroupCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 2px 9px;
`;

const AssignRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr 100px auto;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 56px;
  border-top: 1px solid #f1f5f9;
  transition: background 0.12s;
  &:hover {
    background: #fafbff;
  }
`;

const AssignCourseTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: #e0f2fe;
  color: #0369a1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 128px;
`;

const AssignCourseName = styled.span`
  font-size: 13px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PeriodBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 6px;
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  white-space: nowrap;
  text-align: center;
`;

const AssignActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CompactEditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1.5px solid #c7d2fe;
  background: #eef2ff;
  color: #4338ca;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 11px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #e0e7ff;
    border-color: #a5b4fc;
  }
`;

const CompactDeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1.5px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 11px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
  }
`;

// Financial year: April (selected year) → March (next year)
// nextYr flag marks Jan/Feb/Mar as belonging to selectedYear+1
const REPORT_MONTHS = [
  { num: '04', name: 'Apr', nextYr: false },
  { num: '05', name: 'May', nextYr: false },
  { num: '06', name: 'Jun', nextYr: false },
  { num: '07', name: 'Jul', nextYr: false },
  { num: '08', name: 'Aug', nextYr: false },
  { num: '09', name: 'Sep', nextYr: false },
  { num: '10', name: 'Oct', nextYr: false },
  { num: '11', name: 'Nov', nextYr: false },
  { num: '12', name: 'Dec', nextYr: false },
  { num: '01', name: 'Jan', nextYr: true },
  { num: '02', name: 'Feb', nextYr: true },
  { num: '03', name: 'Mar', nextYr: true },
];

// Returns the set of monthKey strings (YYYY-MM) for a financial year (Apr yr – Mar yr+1)
function getFyMonthKeys(yr) {
  const y = Number(yr);
  const nextY = y + 1;
  return new Set(
    REPORT_MONTHS.map((m) => `${m.nextYr ? nextY : y}-${m.num}`)
  );
}

const formatReportNumber = (val) => {
  if (val == null) return null;
  return Number(val).toLocaleString();
};

const ReportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StaffBlock = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const StaffBlockHeader = styled.div`
  background: linear-gradient(120deg, #1e3a5f, #2563eb);
  color: #ffffff;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const ReportTableWrapper = styled.div`
  overflow-x: auto;
`;

const ReportTable = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  font-size: 11px;
  white-space: nowrap;
`;

const RThMonth = styled.th`
  padding: 6px 10px;
  text-align: center;
  background: #e8edf7;
  border: 1px solid #d1d9f0;
  font-weight: 700;
  font-size: 11px;
  color: #1e3a8a;
`;

const RThSub = styled.th`
  padding: 5px 6px;
  text-align: center;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  min-width: 54px;
`;

const RThLabel = styled.th`
  padding: 6px 12px;
  text-align: left;
  background: #e8edf7;
  border: 1px solid #d1d9f0;
  font-weight: 700;
  font-size: 11px;
  color: #1e3a8a;
  min-width: 180px;
  max-width: 260px;
`;

const RTd = styled.td`
  padding: 5px 7px;
  text-align: center;
  border: 1px solid #e2e8f0;
  font-size: 11px;
  color: ${({ $empty }) => ($empty ? '#cbd5e1' : '#0f172a')};
  background: ${({ $stripe }) => ($stripe ? '#f8fafc' : '#ffffff')};
`;

const RTdName = styled.td`
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #1e40af;
  background: ${({ $stripe }) => ($stripe ? '#f8fafc' : '#ffffff')};
  white-space: normal;
  min-width: 180px;
  max-width: 260px;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s;
  &:hover {
    background: #eff6ff !important;
    color: #1d4ed8;
  }
`;

const RThGrowth = styled.th`
  padding: 5px 6px;
  text-align: center;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  font-size: 10px;
  font-weight: 600;
  color: #065f46;
  min-width: 46px;
`;

const RTdGrowth = styled.td`
  padding: 5px 7px;
  text-align: center;
  border: 1px solid #e2e8f0;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $stripe }) => ($stripe ? '#f8fafc' : '#ffffff')};
  color: ${({ $sign }) =>
    $sign === 'pos' ? '#16a34a' : $sign === 'neg' ? '#dc2626' : '#94a3b8'};
`;

const RThYearGroup = styled.th`
  padding: 6px 10px;
  text-align: center;
  background: #fef3c7;
  border: 1px solid #fde68a;
  font-weight: 700;
  font-size: 11px;
  color: #92400e;
`;

const RThYearSub = styled.th`
  padding: 5px 6px;
  text-align: center;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 10px;
  font-weight: 600;
  color: #78350f;
  min-width: 56px;
`;

const RTdYear = styled.td`
  padding: 5px 7px;
  text-align: center;
  border: 1px solid #fde68a;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $stripe }) => ($stripe ? '#fffbeb' : '#fff7ed')};
  color: ${
    ({ $sign }) =>
      $sign === 'pos' ? '#16a34a' :
      $sign === 'neg' ? '#dc2626' :
      $sign === 'neutral' ? '#92400e' : '#94a3b8'
  };
`;

/* ─── Course Detail Modal ─────────────────────────────────── */
const CourseManageCard = styled(ModalCard)`
  max-width: 820px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CmHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 0;
  flex-shrink: 0;
`;

const CmTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  line-height: 1.4;
`;

const CmBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 4px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
`;

const CmSection = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const CmSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #94a3b8;
  margin-bottom: 10px;
`;

const CmStaffRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const CmStaffName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
  min-width: 120px;
`;

const CmChangeStaffBox = styled.div`
  margin-top: 12px;
  padding: 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CmMonthTableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-top: 4px;
`;

const CmMonthTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const CmTh = styled.th`
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #475569;
  padding: 9px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  &:first-child { text-align: left; padding-left: 14px; }
`;

const CmTr = styled.tr`
  &:nth-child(even) td { background: #f8fafc; }
  &:hover td { background: #eff6ff !important; }
`;

const CmTd = styled.td`
  padding: 8px 10px;
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  background: #ffffff;
  &:first-child { text-align: left; padding-left: 14px; }
  &:last-child { white-space: nowrap; }
`;

const CmInput = styled.input`
  width: 80px;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 12px;
  text-align: center;
  background: #ffffff;
  &:focus { border-color: #2563eb; outline: none; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
`;

const CmActBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 9px;
  cursor: pointer;
  gap: 3px;
  transition: all 0.12s;
  &:hover:not(:disabled) { background: #e2e8f0; color: #0f172a; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CmActBtnPrimary = styled(CmActBtn)`
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #4338ca;
  &:hover:not(:disabled) { background: #e0e7ff; border-color: #a5b4fc; }
`;

const CmActBtnDanger = styled(CmActBtn)`
  border-color: #fecaca;
  background: #fff1f2;
  color: #dc2626;
  &:hover:not(:disabled) { background: #fee2e2; border-color: #fca5a5; }
`;

const CmActBtnWarning = styled(CmActBtn)`
  border-color: #fed7aa;
  background: #fff7ed;
  color: #c2410c;
  &:hover:not(:disabled) { background: #ffedd5; border-color: #fdba74; }
`;

const CmBulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: 10px;
`;

/* ── New: Download/Upload/AddTarget modals ─────────────────── */

const TextInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #d0d7ff;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37,99,235,0.12);
  }
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 14px;
`;

const DropZone = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;
  &:hover {
    border-color: #6366f1;
    background: #eef2ff;
  }
`;

const UlBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 5px;
  background: ${(p) => (p.$success ? '#dcfce7' : p.$error ? '#fee2e2' : '#f1f5f9')};
  color: ${(p) => (p.$success ? '#15803d' : p.$error ? '#dc2626' : '#64748b')};
  border: 1px solid ${(p) => (p.$success ? '#bbf7d0' : p.$error ? '#fecaca' : '#e2e8f0')};
`;

const WideModalCard = styled(ModalCard)`
  max-width: 860px;
`;

const LOGIN_STATUS_PRESETS = ['active', 'blocked'];

const statusColorMap = {
  active: { bg: '#dcfce7', color: '#15803d' },
  blocked: { bg: '#fee2e2', color: '#b91c1c' },
  inactive: { bg: '#fef9c3', color: '#b45309' },
  pending: { bg: '#ede9fe', color: '#7c3aed' },
  deleted: { bg: '#f3f4f6', color: '#374151' },
};

const getInitials = (value) => {
  if (!value) return 'ST';
  return value
    .split(/\s+/)
    .filter((part) => Boolean(part) && /[A-Za-z0-9]/.test(part[0]))
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || 'ST';
};

const formatStatusLabel = (value) => {
  if (!value) return 'Unknown';
  const normalized = String(value).trim();
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatMonthKeyLabel = (value) => {
  if (!value) {
    return '—';
  }
  const [year, monthPart] = value.split('-');
  const monthIndex = Number.parseInt(monthPart, 10);
  if (!year || !Number.isInteger(monthIndex) || monthIndex < 1 || monthIndex > 12) {
    return value;
  }
  return `${MONTH_NAMES[monthIndex - 1]} ${year}`;
};

const currentYear = String(new Date().getFullYear());

const defaultQuery = {
  page: 1,
  pageSize: 10,
  year: currentYear,
  sortBy: 'period',
  sortDir: 'desc',
};

export default function AdminSalesStaffCourseMapping() {
  const [query, setQuery] = useState(defaultQuery);
  const [searchInput, setSearchInput] = useState('');
  const [courseShortNameSearch, setCourseShortNameSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, page: 1, pageSize: 10, sortBy: 'period', sortDir: 'desc' });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({ staff: [], courseCodes: [], periods: [], assignments: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [formData, setFormData] = useState({ staffId: null, courseCodeId: null, monthKey: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffStatusFilter, setStaffStatusFilter] = useState('all');
  const [staffImageErrors, setStaffImageErrors] = useState({});
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const fileInputRef = useRef(null);

  // Course detail modal (double-click on RTdName)
  const [cdModal, setCdModal] = useState(null);
  // { staff: {id, name}, course: {id, shortName, fullName, months: {}} }
  const [cdChangeStaff, setCdChangeStaff] = useState(false);
  const [cdNewStaffId, setCdNewStaffId] = useState(null);
  const [cdSaving, setCdSaving] = useState(false);
  const [cdEditMonth, setCdEditMonth] = useState(null);
  // { num, exReg, acReg, exInc, acInc, mappingId, monthlyIncomeId }
  const [cdSelectedMonths, setCdSelectedMonths] = useState(new Set());
  const [cdBulkTransferStaffId, setCdBulkTransferStaffId] = useState(null);

  // ── Download Monthly Target Template modal ─────────────────
  const [dlModalOpen, setDlModalOpen] = useState(false);
  const [dlForm, setDlForm] = useState({ startMonth: '', endMonth: '', staffId: 'all' });
  const [dlErrors, setDlErrors] = useState({});
  const [dlLoading, setDlLoading] = useState(false);
  const [dlPreviewRows, setDlPreviewRows] = useState([]);
  const [dlPreviewLoading, setDlPreviewLoading] = useState(false);
  const [dlPreviewError, setDlPreviewError] = useState('');

  // ── Upload Monthly Target Template modal ────────────────────
  const [ulModalOpen, setUlModalOpen] = useState(false);
  const [ulRows, setUlRows] = useState([]);
  const [ulFileMeta, setUlFileMeta] = useState(null);
  const [ulSelectedFile, setUlSelectedFile] = useState(null);
  const [ulParsing, setUlParsing] = useState(false);
  const [ulUploading, setUlUploading] = useState(false);
  const ulFileInputRef = useRef(null);

  // ── Add Target modal ────────────────────────────────────────
  const [atModalOpen, setAtModalOpen] = useState(false);
  const [atForm, setAtForm] = useState({ mappingId: null, exReg: '0', exInc: '0', acReg: '', acInc: '' });
  const [atErrors, setAtErrors] = useState({});
  const [atSubmitting, setAtSubmitting] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function loadOptions() {
      try {
        setOptionsLoading(true);
        const data = await getStaffCourseMappingOptions();
        if (!ignore) {
          setOptions({
            staff: data?.staff || [],
            courseCodes: data?.courseCodes || [],
            periods: data?.periods || [],
            assignments: data?.assignments || [],
          });
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.response?.data?.message || 'Failed to load filter options.');
        }
      } finally {
        if (!ignore) setOptionsLoading(false);
      }
    }
    loadOptions();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    async function loadMappings() {
      try {
        setLoading(true);
        setError('');
        const response = await listStaffCourseMappings(query);
        if (ignore) return;
        setRecords(response?.data || []);
        setMeta(response?.meta || { total: 0, page: query.page, pageSize: query.pageSize, totalPages: 1 });
        setSummary(response?.summary || null);
      } catch (err) {
        if (!ignore) {
          setError(err?.response?.data?.message || 'Failed to load staff course mappings.');
          setRecords([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    loadMappings();
    return () => {
      ignore = true;
    };
  }, [query, reloadKey]);

  useEffect(() => {
    if (!query.year || query.year === 'all') {
      setReportData(null);
      return () => {};
    }
    let ignore = false;
    async function loadReport() {
      try {
        setReportLoading(true);
        setReportError('');
        const data = await getStaffCourseMappingReport(query.year);
        if (!ignore) setReportData(data);
      } catch (err) {
        if (!ignore) setReportError(err?.response?.data?.message || 'Failed to load year report.');
      } finally {
        if (!ignore) setReportLoading(false);
      }
    }
    loadReport();
    return () => { ignore = true; };
  }, [query.year, reloadKey]);

  const modalStaffOptions = useMemo(() => (
    options.staff.map((staff) => ({
      value: staff.id,
      label: `${staff.name}${staff.position ? ` · ${staff.position}` : ''}`,
      meta: staff,
    }))
  ), [options.staff]);

  const staffIdSet = useMemo(() => new Set((options.staff || []).map((item) => Number(item.id)).filter(Number.isFinite)), [options.staff]);
  const courseIdSet = useMemo(() => new Set((options.courseCodes || []).map((item) => Number(item.id)).filter(Number.isFinite)), [options.courseCodes]);
  const existingAssignmentsSet = useMemo(
    () => new Set(
      (options.assignments || [])
        .filter((item) => item.staffId && item.courseCodeId && item.monthKey)
        .map((item) => `${item.staffId}-${item.courseCodeId}-${item.monthKey}`)
    ),
    [options.assignments]
  );

  const assignedStaffIds = useMemo(() => {
    const ids = new Set();
    options.assignments.forEach((assignment) => {
      if (assignment.staffId) {
        ids.add(Number(assignment.staffId));
      }
    });
    return ids;
  }, [options.assignments]);

  const staffFilterOptions = useMemo(() => {
    const source = assignedStaffIds.size > 0
      ? options.staff.filter((staff) => assignedStaffIds.has(staff.id))
      : options.staff;
    return source.map((staff) => ({
      value: staff.id,
      label: `${staff.name}${staff.position ? ` · ${staff.position}` : ''}`,
      meta: staff,
    }));
  }, [options.staff, assignedStaffIds]);

  const modalCourseOptions = useMemo(() => (
    options.courseCodes.map((course) => ({
      value: course.id,
      label: course.shortName ? `${course.shortName} — ${course.fullName}` : course.fullName,
      meta: course,
    }))
  ), [options.courseCodes]);

  // Course IDs already assigned in the selected year (for "hide already added" filter)
  const assignedCourseIdsInYear = useMemo(() => {
    const yr = query.year && query.year !== 'all' ? String(query.year) : null;
    if (!yr) return new Set();
    const fyKeys = getFyMonthKeys(yr);
    return new Set(
      (options.assignments || [])
        .filter((a) => a.monthKey && fyKeys.has(a.monthKey))
        .map((a) => Number(a.courseCodeId))
    );
  }, [options.assignments, query.year]);

  const modalCourseOptionsFiltered = useMemo(() => {
    if (showAllCourses || !query.year || query.year === 'all' || assignedCourseIdsInYear.size === 0) {
      return modalCourseOptions;
    }
    // When editing keep the current course visible even if already assigned
    const editingId = activeRecord ? Number(activeRecord?.courseCode?.id) : null;
    return modalCourseOptions.filter(
      (opt) => !assignedCourseIdsInYear.has(Number(opt.value)) || Number(opt.value) === editingId
    );
  }, [modalCourseOptions, showAllCourses, assignedCourseIdsInYear, query.year, activeRecord]);

  const courseFilterOptions = useMemo(() => {
    let filteredAssignments = options.assignments;
    if (query.staffId && query.staffId !== 'all') {
      filteredAssignments = filteredAssignments.filter((assignment) => (
        String(assignment.staffId) === String(query.staffId)
      ));
    }
    const allowedCourseIds = new Set(
      filteredAssignments
        .map((assignment) => assignment.courseCodeId)
        .filter((id) => id || id === 0)
    );

    const globalCourseIds = new Set(
      options.assignments
        .map((assignment) => assignment.courseCodeId)
        .filter((id) => id || id === 0)
    );

    const sourceCourses = options.courseCodes.filter((course) => {
      if (allowedCourseIds.size > 0) {
        return allowedCourseIds.has(course.id);
      }
      if (globalCourseIds.size > 0) {
        return globalCourseIds.has(course.id);
      }
      return true;
    });

    return sourceCourses.map((course) => ({
      value: course.id,
      label: course.shortName ? `${course.shortName} — ${course.fullName}` : course.fullName,
      meta: course,
    }));
  }, [options.courseCodes, options.assignments, query.staffId]);

  const yearFilterOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= 2020; y--) {
      years.push({ value: String(y), label: String(y) });
    }
    return years;
  }, []);

  const periodFilterOptions = useMemo(() => {
    let filteredAssignments = options.assignments;
    if (query.staffId && query.staffId !== 'all') {
      filteredAssignments = filteredAssignments.filter((assignment) => (
        String(assignment.staffId) === String(query.staffId)
      ));
    }
    if (query.courseCodeId && query.courseCodeId !== 'all') {
      filteredAssignments = filteredAssignments.filter((assignment) => (
        String(assignment.courseCodeId) === String(query.courseCodeId)
      ));
    }
    if (query.year && query.year !== 'all') {
      filteredAssignments = filteredAssignments.filter((assignment) =>
        assignment.monthKey && assignment.monthKey.startsWith(`${query.year}-`)
      );
    }

    const monthKeys = Array.from(new Set(
      filteredAssignments
        .map((assignment) => assignment.monthKey)
        .filter((value) => Boolean(value))
    ));

    if (monthKeys.length === 0) {
      const basePeriods = query.year && query.year !== 'all'
        ? options.periods.filter((p) => p.key && p.key.startsWith(`${query.year}-`))
        : options.periods;
      return basePeriods.map((period) => ({ value: period.key, label: period.label }));
    }

    const items = monthKeys.map((key) => ({ value: key, label: formatMonthKeyLabel(key) }));
    items.sort((a, b) => (a.value > b.value ? -1 : 1));
    return items;
  }, [options.assignments, options.periods, query.staffId, query.courseCodeId, query.year]);

  const loginStatusOptions = useMemo(() => {
    const values = new Set(LOGIN_STATUS_PRESETS);
    (options.staff || []).forEach((staff) => {
      if (staff.loginStatus) {
        values.add(String(staff.loginStatus).toLowerCase());
      }
    });
    return Array.from(values).filter(Boolean).sort();
  }, [options.staff]);

  const staffFilterValue = useMemo(() => (
    staffFilterOptions.find((opt) => String(opt.value) === String(query.staffId)) || null
  ), [staffFilterOptions, query.staffId]);

  const courseFilterValue = useMemo(() => (
    courseFilterOptions.find((opt) => String(opt.value) === String(query.courseCodeId)) || null
  ), [courseFilterOptions, query.courseCodeId]);

  const monthFilterValue = useMemo(() => (
    periodFilterOptions.find((opt) => opt.value === query.monthKey) || null
  ), [periodFilterOptions, query.monthKey]);

  const loginStatusFilterOptions = useMemo(() => (
    loginStatusOptions.map((status) => ({ value: status, label: formatStatusLabel(status) }))
  ), [loginStatusOptions]);

  const loginStatusFilterValue = useMemo(() => (
    loginStatusFilterOptions.find((opt) => opt.value === query.loginStatus) || null
  ), [loginStatusFilterOptions, query.loginStatus]);

  const modalStaffValue = useMemo(() => (
    modalStaffOptions.find((opt) => String(opt.value) === String(formData.staffId)) || null
  ), [modalStaffOptions, formData.staffId]);

  const modalCourseValue = useMemo(() => (
    modalCourseOptions.find((opt) => String(opt.value) === String(formData.courseCodeId)) || null
  ), [modalCourseOptions, formData.courseCodeId]);

  const staffStatusOptions = useMemo(() => (
    ['all', ...loginStatusOptions]
  ), [loginStatusOptions]);

  const recordsByStaff = useMemo(() => {
    const map = new Map();
    for (const record of records) {
      const sid = record?.staff?.id;
      if (!sid) continue;
      if (!map.has(sid)) {
        map.set(sid, { staff: record.staff, assignments: [] });
      }
      map.get(sid).assignments.push(record);
    }
    return Array.from(map.values());
  }, [records]);

  const staffDirectoryItems = useMemo(() => {
    const list = options.staff || [];
    const queryText = staffSearch.trim().toLowerCase();
    return list.filter((staff) => {
      const normalizedStatus = staff.loginStatus ? String(staff.loginStatus).toLowerCase() : '';
      const matchesStatus = staffStatusFilter === 'all' ? true : normalizedStatus === staffStatusFilter;
      if (!matchesStatus) {
        return false;
      }
      if (!queryText) {
        return true;
      }
      const fields = [
        staff.name,
        staff.id,
        staff.employeeNumber,
        staff.email,
        staff.position,
        staff.type,
        staff.loginStatus,
      ];
      return fields.some((field) => field && String(field).toLowerCase().includes(queryText));
    });
  }, [options.staff, staffSearch, staffStatusFilter]);

  const courseDirectoryItems = useMemo(() => {
    const list = options.courseCodes || [];
    const queryText = courseSearch.trim().toLowerCase();
    if (!queryText) return list;
    return list.filter((course) => {
      const fields = [course.shortName, course.fullName, course.code, course.id];
      return fields.some((field) => field && String(field).toLowerCase().includes(queryText));
    });
  }, [options.courseCodes, courseSearch]);

  useEffect(() => {
    if (optionsLoading) return;
    if (query.staffId !== 'all' && !staffFilterOptions.some((opt) => String(opt.value) === String(query.staffId))) {
      setQuery((prev) => ({ ...prev, staffId: 'all', page: 1 }));
    }
  }, [optionsLoading, staffFilterOptions, query.staffId]);

  useEffect(() => {
    if (optionsLoading) return;
    if (query.courseCodeId !== 'all' && !courseFilterOptions.some((opt) => String(opt.value) === String(query.courseCodeId))) {
      setQuery((prev) => ({ ...prev, courseCodeId: 'all', page: 1 }));
    }
  }, [optionsLoading, courseFilterOptions, query.courseCodeId]);

  useEffect(() => {
    if (query.year !== 'all' && !yearFilterOptions.some((opt) => opt.value === query.year)) {
      setQuery((prev) => ({ ...prev, year: 'all', monthKey: 'all', page: 1 }));
    }
  }, [yearFilterOptions, query.year]);

  useEffect(() => {
    if (optionsLoading) return;
    if (query.monthKey !== 'all' && !periodFilterOptions.some((opt) => opt.value === query.monthKey)) {
      setQuery((prev) => ({ ...prev, monthKey: 'all', page: 1 }));
    }
  }, [optionsLoading, periodFilterOptions, query.monthKey]);

  const summaryItems = useMemo(() => {
    if (!summary) return [];
    return [
      {
        label: 'Total Mappings',
        value: summary.totalMappings,
        hint: 'Assignments across all cycles',
      },
      {
        label: 'Unique Staff',
        value: summary.uniqueStaff,
        hint: 'Advisors with at least one mapping',
      },
      {
        label: 'Course Codes',
        value: summary.uniqueCourseCodes,
        hint: 'Distinct codes allocated',
      },
      {
        label: summary.currentPeriodLabel || 'Current Cycle',
        value: summary.currentPeriodCount,
        hint: 'Mappings for this month',
      },
    ];
  }, [summary]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: searchInput.trim(),
    }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setCourseShortNameSearch('');
    setQuery(defaultQuery);
  };

  const handleSelectChange = (key) => (option) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      [key]: option ? option.value : 'all',
    }));
  };

  const handleYearChange = (option) => {
    const selectedYear = option ? option.value : 'all';
    setQuery((prev) => {
      const next = { ...prev, page: 1, year: selectedYear };
      if (selectedYear !== 'all' && prev.monthKey !== 'all' && !prev.monthKey.startsWith(`${selectedYear}-`)) {
        next.monthKey = 'all';
      }
      return next;
    });
  };

  const handleSort = (key) => {
    setQuery((prev) => ({
      ...prev,
      sortBy: key,
      sortDir: prev.sortBy === key && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (nextPage) => {
    setQuery((prev) => ({
      ...prev,
      page: nextPage,
    }));
  };

  const handlePageSizeChange = (event) => {
    const size = Number(event.target.value) || 10;
    setQuery((prev) => ({
      ...prev,
      pageSize: size,
      page: 1,
    }));
  };

  const openStaffDirectory = () => {
    setStaffSearch('');
    setStaffStatusFilter('all');
    setStaffImageErrors({});
    setStaffModalOpen(true);
  };

  const closeStaffDirectory = () => {
    setStaffModalOpen(false);
  };

  const openCourseDirectory = () => {
    setCourseSearch('');
    setCourseModalOpen(true);
  };

  const closeCourseDirectory = () => {
    setCourseModalOpen(false);
  };

  // Export the financial-year report grid exactly as shown on screen
  const handleDownloadReport = () => {
    if (!reportData?.data?.length || !query.year || query.year === 'all') {
      Swal.fire('No report loaded', 'Select a year to load the report first.', 'info');
      return;
    }
    try {
      const yr = query.year;
      const nextYr = Number(yr) + 1;
      const MONTH_ORDER = ['04','05','06','07','08','09','10','11','12','01','02','03'];
      const MONTH_LABEL = (m) => {
        const names = { '04':'Apr','05':'May','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec','01':'Jan','02':'Feb','03':'Mar' };
        const isNext = ['01','02','03'].includes(m);
        return `${names[m]} '${String(isNext ? nextYr : yr).slice(-2)}`;
      };

      // Apply same short-name filter that's currently active on screen
      const snFilter = courseShortNameSearch.trim().toLowerCase();
      const filtered = snFilter
        ? reportData.data.map((s) => ({ ...s, courses: s.courses.filter((c) => c.shortName?.toLowerCase().includes(snFilter)) })).filter((s) => s.courses.length > 0)
        : reportData.data;

      // Build header rows (mirrors the 2-row thead on screen)
      const headerRow1 = ['Staff Name', 'Course Short Name', 'Course Full Name'];
      const headerRow2 = ['', '', ''];
      for (const m of MONTH_ORDER) {
        const lbl = MONTH_LABEL(m);
        headerRow1.push(lbl, '', '', '', '', '');
        headerRow2.push('Ex Reg', 'Ac Reg', 'Reg Δ', 'Ex Inc', 'Ac Inc', 'Inc Δ');
      }
      headerRow1.push('Year Total', '', '', '', '', '');
      headerRow2.push('Ex Reg', 'Ac Reg', 'Reg Δ', 'Ex Inc', 'Ac Inc', 'Inc Δ');

      const dataRows = [];
      for (const staff of filtered) {
        for (const course of staff.courses) {
          let ytExReg = 0, ytAcReg = 0, ytExInc = 0, ytAcInc = 0;
          const cells = [];
          for (const m of MONTH_ORDER) {
            const d = course.months[m];
            const exReg = d?.exReg != null ? d.exReg : '';
            const acReg = d?.acReg != null ? d.acReg : '';
            const exInc = d?.exInc != null ? d.exInc : '';
            const acInc = d?.acInc != null ? d.acInc : '';
            const regDelta = (d?.exReg != null && d?.acReg != null) ? d.acReg - d.exReg : '';
            const incDelta = (d?.exInc != null && d?.acInc != null) ? d.acInc - d.exInc : '';
            cells.push(exReg, acReg, regDelta, exInc, acInc, incDelta);
            if (d?.exReg != null) ytExReg += d.exReg;
            if (d?.acReg != null) ytAcReg += d.acReg;
            if (d?.exInc != null) ytExInc += d.exInc;
            if (d?.acInc != null) ytAcInc += d.acInc;
          }
          const ytRegDelta = ytExReg || ytAcReg ? ytAcReg - ytExReg : '';
          const ytIncDelta = ytExInc || ytAcInc ? ytAcInc - ytExInc : '';
          dataRows.push([
            staff.name,
            course.shortName || '',
            course.fullName || '',
            ...cells,
            ytExReg || '', ytAcReg || '', ytRegDelta,
            ytExInc || '', ytAcInc || '', ytIncDelta,
          ]);
        }
      }

      const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);

      // Merge month header cells across 6 sub-columns each
      ws['!merges'] = [];
      let col = 3; // after Staff, Course Short, Course Full
      for (let i = 0; i < MONTH_ORDER.length + 1; i++) { // +1 for Year Total
        ws['!merges'].push({ s: { r: 0, c: col }, e: { r: 0, c: col + 5 } });
        col += 6;
      }
      // Also merge the first 3 header cells vertically
      ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
      ws['!merges'].push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });
      ws['!merges'].push({ s: { r: 0, c: 2 }, e: { r: 1, c: 2 } });

      // Column widths
      ws['!cols'] = [
        { wch: 24 }, { wch: 20 }, { wch: 34 },
        ...Array((MONTH_ORDER.length + 1) * 6).fill({ wch: 12 }),
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `FY ${yr}-${String(nextYr).slice(-2)}`);

      // Info sheet
      const wsInfo = XLSX.utils.aoa_to_sheet([
        ['Financial Year', `Apr ${yr} – Mar ${nextYr}`],
        ['Exported At', new Date().toLocaleString()],
        ['Staff Count', filtered.length],
        ['Course Assignments', filtered.reduce((s, st) => s + st.courses.length, 0)],
        ...(snFilter ? [['Filter Applied', `Course Short Name contains "${snFilter}"`]] : []),
      ]);
      wsInfo['!cols'] = [{ wch: 22 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsInfo, 'Info');

      XLSX.writeFile(wb, `Assignment_Report_FY${yr}-${String(nextYr).slice(-2)}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Failed to download report', err);
      Swal.fire('Download failed', 'Unable to create the Excel file.', 'error');
    }
  };

  const handleDownloadExcel = async () => {
    // If a year is selected and the report is loaded, export the full financial-year view
    if (query.year && query.year !== 'all' && reportData?.data?.length) {
      try {
        const wb = XLSX.utils.book_new();

        /* ── Sheet 1: Full financial-year report ─────────────── */
        const MONTH_ORDER = ['04','05','06','07','08','09','10','11','12','01','02','03'];
        const MONTH_LABELS = { '04':'Apr','05':'May','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec','01':'Jan','02':'Feb','03':'Mar' };

        const yr = query.year;
        const nextYr = Number(yr) + 1;

        const headerRow = [
          'Staff Name',
          'Course Short Name',
          'Course Full Name',
          ...MONTH_ORDER.flatMap((m) => {
            const label = `${MONTH_LABELS[m]} '${String(['01','02','03'].includes(m) ? nextYr : yr).slice(-2)}`;
            return [
              `${label} — Ex. Reg`,
              `${label} — Ac. Reg`,
              `${label} — Ex. Income`,
              `${label} — Ac. Income`,
            ];
          }),
          'Total Ex. Reg',
          'Total Ac. Reg',
          'Total Ex. Income',
          'Total Ac. Income',
        ];

        const dataRows = [];
        for (const staff of reportData.data) {
          for (const course of staff.courses) {
            let totalExReg = 0, totalAcReg = 0, totalExInc = 0, totalAcInc = 0;
            const monthCells = MONTH_ORDER.flatMap((m) => {
              const d = course.months[m];
              const exReg = d?.exReg != null ? d.exReg : '';
              const acReg = d?.acReg != null ? d.acReg : '';
              const exInc = d?.exInc != null ? d.exInc : '';
              const acInc = d?.acInc != null ? d.acInc : '';
              if (d?.exReg != null) totalExReg += d.exReg;
              if (d?.acReg != null) totalAcReg += d.acReg;
              if (d?.exInc != null) totalExInc += d.exInc;
              if (d?.acInc != null) totalAcInc += d.acInc;
              return [exReg, acReg, exInc, acInc];
            });
            dataRows.push([
              staff.name,
              course.shortName || '',
              course.fullName || '',
              ...monthCells,
              totalExReg || '',
              totalAcReg || '',
              totalExInc || '',
              totalAcInc || '',
            ]);
          }
        }

        const ws1 = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

        // Column widths
        ws1['!cols'] = [
          { wch: 26 }, // Staff Name
          { wch: 18 }, // Course Short
          { wch: 34 }, // Course Full
          ...MONTH_ORDER.flatMap(() => [{ wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }]),
          { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
        ];

        XLSX.utils.book_append_sheet(wb, ws1, `FY ${yr}-${String(nextYr).slice(-2)} Report`);

        /* ── Sheet 2: Summary per staff ──────────────────────── */
        const summaryHeader = ['Staff Name', 'Course Short Name', 'Months Assigned', 'Months with Targets', 'Total Ex. Income', 'Total Ac. Income', 'Total Ex. Reg', 'Total Ac. Reg'];
        const summaryRows = [];
        for (const staff of reportData.data) {
          for (const course of staff.courses) {
            const monthValues = Object.values(course.months);
            const assigned = monthValues.length;
            const withTargets = monthValues.filter((d) => d?.monthlyIncomeId).length;
            const exInc = monthValues.reduce((s, d) => s + (d?.exInc ?? 0), 0);
            const acInc = monthValues.reduce((s, d) => s + (d?.acInc ?? 0), 0);
            const exReg = monthValues.reduce((s, d) => s + (d?.exReg ?? 0), 0);
            const acReg = monthValues.reduce((s, d) => s + (d?.acReg ?? 0), 0);
            summaryRows.push([staff.name, course.shortName || '', assigned, withTargets, exInc || '', acInc || '', exReg || '', acReg || '']);
          }
        }
        const ws2 = XLSX.utils.aoa_to_sheet([summaryHeader, ...summaryRows]);
        ws2['!cols'] = [{ wch: 26 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

        /* ── Meta sheet ───────────────────────────────────────── */
        const ws3 = XLSX.utils.aoa_to_sheet([
          ['Report Type', 'Staff Course Mapping — Financial Year Report'],
          ['Financial Year', `Apr ${yr} – Mar ${nextYr}`],
          ['Exported At', new Date().toLocaleString()],
          ['Total Staff', reportData.data.length],
          ['Total Course Assignments', reportData.data.reduce((s, st) => s + st.courses.length, 0)],
        ]);
        ws3['!cols'] = [{ wch: 24 }, { wch: 44 }];
        XLSX.utils.book_append_sheet(wb, ws3, 'Info');

        XLSX.writeFile(wb, `Staff_Course_Mapping_FY${yr}-${String(nextYr).slice(-2)}_${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (err) {
        console.error('Failed to download Excel', err);
        Swal.fire('Download failed', 'Unable to create the Excel file. Please try again.', 'error');
      }
      return;
    }

    // Fallback: simple assignment list from paginated records
    if (!records || records.length === 0) {
      Swal.fire('Nothing to download', 'No mappings are available for the current filters.', 'info');
      return;
    }
    try {
      const headers = ['Staff Name', 'Staff Position', 'Staff Email', 'Staff Login Status', 'Course Short Name', 'Course Full Name', 'Period Label', 'Period Key'];
      const rows = records.map((r) => [
        r?.staff?.name?.trim() || '',
        r?.staff?.position || r?.staff?.type || '',
        r?.staff?.email || '',
        r?.staff?.loginStatus || '',
        r?.courseCode?.shortName || '',
        r?.courseCode?.fullName || '',
        r?.period?.label || '',
        r?.period?.key || '',
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [{ wch: 26 }, { wch: 20 }, { wch: 30 }, { wch: 16 }, { wch: 18 }, { wch: 34 }, { wch: 16 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Staff Course Mapping');
      XLSX.writeFile(wb, `Staff_Course_Mapping_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Failed to download Excel', err);
      Swal.fire('Download failed', 'Unable to create the Excel file. Please try again.', 'error');
    }
  };

  const copyStaffId = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      Swal.fire({
        title: 'Copied to clipboard',
        text: 'The Staff ID has been copied successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        timer: 1600,
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: (popup) => {
          popup.style.zIndex = 3000;
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '3000';
          }
        },
      });
    } catch (err) {
      console.error('Failed to copy staff ID', err);
      Swal.fire('Copy failed', 'Unable to copy the Staff ID. Please try again.', 'error');
    }
  };

  const copyStaffName = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      Swal.fire({
        title: 'Copied to clipboard',
        text: 'The staff name has been copied successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        timer: 1600,
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: (popup) => {
          popup.style.zIndex = 3000;
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '3000';
          }
        },
      });
    } catch (err) {
      console.error('Failed to copy staff name', err);
      Swal.fire('Copy failed', 'Unable to copy the staff name. Please try again.', 'error');
    }
  };

  const copyCourseId = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      Swal.fire({
        title: 'Copied to clipboard',
        text: 'The Course ID has been copied successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        timer: 1600,
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: (popup) => {
          popup.style.zIndex = 3000;
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '3000';
          }
        },
      });
    } catch (err) {
      console.error('Failed to copy course ID', err);
      Swal.fire('Copy failed', 'Unable to copy the Course ID. Please try again.', 'error');
    }
  };

  const copyCourseShortName = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      Swal.fire({
        title: 'Copied to clipboard',
        text: 'The course short name has been copied successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        timer: 1600,
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: (popup) => {
          popup.style.zIndex = 3000;
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '3000';
          }
        },
      });
    } catch (err) {
      console.error('Failed to copy course short name', err);
      Swal.fire('Copy failed', 'Unable to copy the course short name. Please try again.', 'error');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const XLSX = await import('xlsx');

      const templateHeaders = [
        'Staff ID (required)',
        'Staff Name (optional)',
        'Course Code ID (required)',
        'Course Short Name (optional)',
        'Assignment Month Number (1-12, required)',
        'Assignment Year (YYYY, required)',
      ];

      const templateSheet = XLSX.utils.aoa_to_sheet([templateHeaders]);

      const staffHeaders = ['Staff ID', 'Name', 'Position', 'Type', 'Office Email', 'Employee Number', 'Login Status'];
      const staffRows = (options.staff || []).map((staff) => [
        staff.id ?? '',
        staff.name ?? '',
        staff.position ?? '',
        staff.type ?? '',
        staff.email ?? '',
        staff.employeeNumber ?? '',
        staff.loginStatus ?? '',
      ]);
      const staffSheet = XLSX.utils.aoa_to_sheet([[...staffHeaders], ...staffRows]);

      const courseHeaders = ['Course Code ID', 'Short Name', 'Full Name'];
      const courseRows = (options.courseCodes || []).map((course) => [
        course.id ?? '',
        course.shortName ?? '',
        course.fullName ?? '',
      ]);
      const courseSheet = XLSX.utils.aoa_to_sheet([[...courseHeaders], ...courseRows]);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, 'Template');
      XLSX.utils.book_append_sheet(workbook, staffSheet, 'Staff List');
      XLSX.utils.book_append_sheet(workbook, courseSheet, 'Course Codes');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Staff_Course_Mapping_Template_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download staff course mapping template', err);
      Swal.fire('Download failed', 'Unable to create the template. Please try again.', 'error');
    }
  };

  const normalizeNumber = (value) => {
    if (value === undefined || value === null) return null;
    const text = String(value).trim();
    if (!text) return null;
    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const parseTemplateRow = (row, index, duplicateTracker) => {
    const rowNumber = index + 2; // account for header row

    const staffId = normalizeNumber(
      row['Staff ID (required)'] ?? row['Staff ID'] ?? row['Staff Id'] ?? row['Staff id']
    );
    const courseCodeId = normalizeNumber(
      row['Course Code ID (required)'] ?? row['Course Code ID'] ?? row['Course Code Id']
    );
    const monthNumber = normalizeNumber(
      row['Assignment Month Number (1-12, required)'] ?? row['Assignment Month'] ?? row['Month']
    );
    const year = normalizeNumber(row['Assignment Year (YYYY, required)'] ?? row['Assignment Year'] ?? row['Year']);

    if (!staffId && !courseCodeId && !monthNumber && !year) {
      return { skip: true };
    }

    const errors = [];
    if (!staffId) errors.push('Staff ID is missing');
    if (staffId && !staffIdSet.has(Number(staffId))) errors.push('Staff ID not found in directory');
    if (!courseCodeId) errors.push('Course Code ID is missing');
    if (courseCodeId && !courseIdSet.has(Number(courseCodeId))) errors.push('Course Code ID not found');
    if (!year || String(year).length !== 4) errors.push('Year must be in YYYY format');
    if (!monthNumber || monthNumber < 1 || monthNumber > 12) errors.push('Month must be between 1 and 12');

    const monthKey = year && monthNumber ? `${year}-${String(monthNumber).padStart(2, '0')}` : null;
    const duplicateKey = staffId && courseCodeId && monthKey ? `${staffId}-${courseCodeId}-${monthKey}` : null;

    if (duplicateKey) {
      if (duplicateTracker.has(duplicateKey)) {
        errors.push('Duplicate row in file (same staff, course, and month)');
      } else {
        duplicateTracker.add(duplicateKey);
      }
      if (existingAssignmentsSet.has(duplicateKey)) {
        errors.push('Mapping already exists for this staff, course, and month');
      }
    }

    if (errors.length > 0 || !monthKey) {
      return { error: `Row ${rowNumber}: ${errors.join('; ')}` };
    }

    return {
      rowNumber,
      payload: {
        staffId: Number(staffId),
        courseCodeId: Number(courseCodeId),
        monthKey,
      },
    };
  };

  const handleTemplateUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';

    if (optionsLoading) {
      Swal.fire('Options not ready', 'Please wait for staff and course lists to finish loading.', 'info');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('File too large', 'Please upload a file smaller than 5MB.', 'error');
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !['xlsx', 'xls', 'csv'].includes(extension)) {
      Swal.fire('Unsupported file', 'Only .xlsx, .xls, or .csv files are accepted.', 'warning');
      return;
    }

    try {
      setUploadingTemplate(true);
      Swal.fire({
        title: 'Processing template...',
        text: 'We are reading and validating the uploaded file.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        Swal.fire('No sheets found', 'The uploaded file does not contain any sheets.', 'error');
        return;
      }

      const primarySheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(primarySheet, { header: 1, defval: '' });

      if (!matrix.length) {
        Swal.fire('No data found', 'The template file appears to be empty.', 'info');
        return;
      }

      const headerRow = (matrix[0] || []).map((cell) => String(cell || '').trim());
      const requiredHeaders = [
        'Staff ID (required) || Staff ID || Staff Id || Staff id',
        'Course Code ID (required) || Course Code ID || Course Code Id',
        'Assignment Month Number (1-12, required) || Assignment Month || Month',
        'Assignment Year (YYYY, required) || Assignment Year || Year',
      ];

      const missingHeader = requiredHeaders.find((group) => {
        const variants = group.split('||').map((h) => h.trim());
        return !headerRow.some((cell) => variants.includes(cell));
      });

      if (missingHeader) {
        Swal.fire('Missing columns', `The file must contain these headers: Staff ID, Course Code ID, Assignment Month, Assignment Year.`, 'error');
        return;
      }

      const dataRows = matrix.slice(1);
      if (!dataRows.length) {
        Swal.fire('No data found', 'The template file appears to be empty.', 'info');
        return;
      }

      const rows = dataRows.map((rowArray) => {
        const rowObject = {};
        headerRow.forEach((header, idx) => {
          rowObject[header] = rowArray[idx] ?? '';
        });
        return rowObject;
      });

      if (rows.length === 0) {
        Swal.fire('No data found', 'The template file appears to be empty.', 'info');
        return;
      }

      const duplicateTracker = new Set();
      const parsed = rows.map((row, index) => parseTemplateRow(row, index, duplicateTracker));

      const validationErrors = parsed
        .filter((item) => item && !item.skip && item.error)
        .map((item) => item.error);

      const validPayloads = parsed
        .filter((item) => item && !item.skip && item.payload)
        .map((item) => item);

      if (validPayloads.length === 0) {
        const message = validationErrors.length > 0
          ? validationErrors.slice(0, 5).join('<br>')
          : 'No valid rows were found in the template.';
        Swal.fire('Nothing to upload', message, 'warning');
        return;
      }

      let successCount = 0;
      const failed = [...validationErrors];

      for (const item of validPayloads) {
        try {
          await createStaffCourseMapping(item.payload);
          successCount += 1;
        } catch (err) {
          const apiMessage = err?.response?.data?.message || 'Failed to create mapping';
          failed.push(`Row ${item.rowNumber}: ${apiMessage}`);
        }
      }

      if (Swal.isLoading()) {
        Swal.close();
      }

      setReloadKey((key) => key + 1);

      const errorPreview = failed.slice(0, 6).map((msg) => `<div style="text-align:left">• ${msg}</div>`).join('');
      const extraNote = failed.length > 6 ? `<div style="margin-top:4px">...and ${failed.length - 6} more issues.</div>` : '';

      Swal.fire({
        title: 'Upload complete',
        html: `
          <div style="margin-bottom:8px"><strong>${successCount}</strong> mapping${successCount === 1 ? '' : 's'} created.</div>
          ${failed.length > 0 ? `<div style="text-align:left"><strong>Issues</strong><br>${errorPreview}${extraNote}</div>` : ''}
        `,
        icon: failed.length > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      console.error('Failed to upload template', err);
      if (Swal.isLoading()) {
        Swal.close();
      }
      Swal.fire('Upload failed', 'Unable to process the uploaded file. Please try again.', 'error');
    } finally {
      setUploadingTemplate(false);
    }
  };

  const triggerTemplateUpload = () => {
    if (uploadingTemplate) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const openCreateModal = () => {
    setActiveRecord(null);
    setFormData({ staffId: null, courseCodeId: null, monthKey: '' });
    setFormErrors({});
    setShowAllCourses(false);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setActiveRecord(record);
    setFormData({
      staffId: record?.staff?.id || null,
      courseCodeId: record?.courseCode?.id || null,
      monthKey: record?.period?.key || '',
    });
    setFormErrors({});
    setShowAllCourses(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const handleDelete = async (record) => {
    const result = await Swal.fire({
      title: 'Remove mapping?',
      text: `Unassign ${record?.staff?.name || 'this staff member'} from ${record?.courseCode?.shortName || 'course'} for ${record?.period?.label || 'the selected month'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Delete mapping',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteStaffCourseMapping(record.id);
      Swal.fire({
        title: 'Deleted',
        text: 'Staff course mapping deleted successfully.',
        icon: 'success',
        timer: 1600,
        showConfirmButton: false,
      });
      setReloadKey((key) => key + 1);
    } catch (err) {
      Swal.fire({
        title: 'Unable to delete',
        text: err?.response?.data?.message || 'Please try again later.',
        icon: 'error',
      });
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const payload = {
      staffId: formData.staffId ? Number(formData.staffId) : null,
      courseCodeId: formData.courseCodeId ? Number(formData.courseCodeId) : null,
      monthKey: formData.monthKey,
    };

    const nextErrors = {};
    if (!payload.staffId) nextErrors.staffId = 'Please choose a staff member.';
    if (!payload.courseCodeId) nextErrors.courseCodeId = 'Please choose a course code.';
    if (!payload.monthKey) nextErrors.monthKey = 'Please select an assignment month.';
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    try {
      setSubmitting(true);
      setFormErrors({});
      if (activeRecord) {
        await updateStaffCourseMapping(activeRecord.id, payload);
        Swal.fire({
          title: 'Updated',
          text: 'Mapping updated successfully.',
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        });
      } else {
        await createStaffCourseMapping(payload);
        Swal.fire({
          title: 'Created',
          text: 'Staff course mapping created successfully.',
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        });
      }
      setModalOpen(false);
      setReloadKey((key) => key + 1);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors || {};
      if (Object.keys(apiErrors).length > 0) {
        setFormErrors(apiErrors);
      }
      Swal.fire({
        title: 'Unable to save',
        text: err?.response?.data?.message || 'Please review the form and try again.',
        icon: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Download Monthly Target Template handlers ───────────── */
  const openDlModal = () => {
    setDlForm({ startMonth: '', endMonth: '', staffId: 'all' });
    setDlErrors({});
    setDlPreviewRows([]);
    setDlPreviewError('');
    setDlModalOpen(true);
  };
  const closeDlModal = () => {
    if (dlLoading || dlPreviewLoading) return;
    setDlModalOpen(false);
  };
  const validateDlForm = () => {
    const errs = {};
    if (!dlForm.startMonth) errs.startMonth = 'Start month is required.';
    if (!dlForm.endMonth) errs.endMonth = 'End month is required.';
    if (!errs.startMonth && !errs.endMonth) {
      if (dlForm.startMonth.replace('-', '') > dlForm.endMonth.replace('-', '')) {
        errs.startMonth = 'Start month must be before or equal to end month.';
      }
    }
    setDlErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleDlPreview = async () => {
    if (!validateDlForm() || dlPreviewLoading) return;
    try {
      setDlPreviewLoading(true);
      setDlPreviewError('');
      const res = await previewMonthlyTargetTemplate({
        startMonth: dlForm.startMonth,
        endMonth: dlForm.endMonth,
        staffId: dlForm.staffId !== 'all' ? dlForm.staffId : null,
      });
      setDlPreviewRows(res?.data || []);
    } catch (err) {
      setDlPreviewRows([]);
      setDlPreviewError(err?.response?.data?.message || 'Failed to load preview.');
    } finally {
      setDlPreviewLoading(false);
    }
  };
  const handleDlDownload = async () => {
    if (!validateDlForm() || dlLoading) return;
    try {
      setDlLoading(true);
      const blob = await downloadMonthlyTargetTemplate({
        startMonth: dlForm.startMonth,
        endMonth: dlForm.endMonth,
        staffId: dlForm.staffId !== 'all' ? dlForm.staffId : null,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `monthly-target-template-${dlForm.startMonth}_to_${dlForm.endMonth}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Unable to generate template', err?.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setDlLoading(false);
    }
  };

  /* ── Upload Monthly Target Template handlers ─────────────── */
  const ulReadyRows = useMemo(() => ulRows.filter((r) => r.errors.length === 0), [ulRows]);
  const ulBlockedRows = useMemo(() => ulRows.filter((r) => r.errors.length > 0), [ulRows]);
  const ulSuccessCount = useMemo(() => ulRows.filter((r) => r.status === 'success').length, [ulRows]);
  const ulFailedCount = useMemo(() => ulRows.filter((r) => r.status === 'error').length, [ulRows]);

  const ulResetWorkspace = () => {
    setUlRows([]);
    setUlFileMeta(null);
    setUlSelectedFile(null);
    if (ulFileInputRef.current) ulFileInputRef.current.value = '';
  };
  const openUlModal = () => {
    ulResetWorkspace();
    setUlModalOpen(true);
  };
  const closeUlModal = () => {
    if (ulUploading || ulParsing) return;
    setUlModalOpen(false);
  };

  const ulSanitize = (v) => (v === undefined || v === null ? '' : String(v).replace(/^\uFEFF/, '').trim());
  const ulNormKey = (t) => ulSanitize(t).toLowerCase().replace(/[^a-z0-9]/g, '');
  const ulParseNum = (v, { allowEmpty = true } = {}) => {
    if (v === undefined || v === null) return allowEmpty ? '' : null;
    const t = ulSanitize(v);
    if (!t) return allowEmpty ? '' : null;
    const n = Number(t.replace(/,/g, ''));
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  };

  const ulBuildRow = (obj, rowNum) => {
    const mappingId = (() => { const n = Number(obj.mappingId); return Number.isInteger(n) && n > 0 ? n : null; })();
    const exReg = ulParseNum(ulSanitize(obj.expectedRegistrations), { allowEmpty: false });
    const exInc = ulParseNum(ulSanitize(obj.expectedIncome), { allowEmpty: false });
    const acReg = ulParseNum(ulSanitize(obj.actualRegistrations), { allowEmpty: true });
    const acInc = ulParseNum(ulSanitize(obj.actualIncome), { allowEmpty: true });
    const errors = [];
    if (!mappingId) errors.push('Mapping ID must be a positive integer.');
    if (exReg === null) errors.push('Expected Registrations must be ≥ 0.');
    if (exInc === null) errors.push('Expected Income must be ≥ 0.');
    return {
      rowNumber: rowNum,
      preview: { mappingId: mappingId || ulSanitize(obj.mappingId) || '', expectedRegistrations: ulSanitize(obj.expectedRegistrations), expectedIncome: ulSanitize(obj.expectedIncome), actualRegistrations: ulSanitize(obj.actualRegistrations), actualIncome: ulSanitize(obj.actualIncome) },
      parsed: { mappingId, exReg, exInc, acReg, acInc },
      errors,
      status: errors.length ? 'blocked' : 'pending',
      message: '',
    };
  };

  const ulParseFile = async (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      Swal.fire('Unsupported file', 'Please upload a .xlsx, .xls, or .csv file.', 'warning');
      return;
    }
    setUlParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      if (!wb.SheetNames?.length) throw new Error('Workbook has no sheets.');
      const ws = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, blankrows: false });
      if (!matrix.length) throw new Error('The worksheet is empty.');
      const headerRow = matrix[0].map((c) => ulSanitize(c));
      const keyMap = headerRow.map((c) => {
        const n = ulNormKey(c);
        if (['mappingid', 'staffcoursemappingid', 'mapping'].includes(n)) return 'mappingId';
        if (['expectedregistrations', 'expectingregistrations', 'expectedreg', 'expectedregs'].includes(n)) return 'expectedRegistrations';
        if (['expectedincome', 'plannedincome', 'expectingincome'].includes(n)) return 'expectedIncome';
        if (['actualregistrations', 'actualreg', 'actualregs'].includes(n)) return 'actualRegistrations';
        if (['actualincome', 'realizedincome'].includes(n)) return 'actualIncome';
        return null;
      });
      const dataRows = matrix.slice(1).map((arr, i) => {
        const obj = { mappingId: '', expectedRegistrations: '', expectedIncome: '', actualRegistrations: '', actualIncome: '' };
        keyMap.forEach((k, idx) => { if (k) obj[k] = arr[idx] ?? ''; });
        return obj;
      }).filter((o) => !(o.mappingId === '' && o.expectedRegistrations === '' && o.expectedIncome === ''));
      if (!dataRows.length) throw new Error('No data rows found.');
      const parsed = dataRows.map((o, i) => ulBuildRow(o, i + 2));
      setUlRows(parsed);
      setUlSelectedFile(file);
      setUlFileMeta({ name: file.name, sheet: wb.SheetNames[0], total: parsed.length });
      const issues = parsed.filter((r) => r.errors.length > 0).length;
      Swal.fire({
        title: issues ? 'Parsed with issues' : 'File ready',
        text: `${parsed.length} rows loaded${issues ? `, ${issues} need fixes.` : '.'}`,
        icon: issues ? 'warning' : 'success',
        didOpen: () => { const c = Swal.getContainer(); if (c) c.style.zIndex = '3000'; },
      });
    } catch (err) {
      ulResetWorkspace();
      Swal.fire({
        title: 'Cannot read file',
        text: err.message || 'Check the template format.',
        icon: 'error',
        didOpen: () => { const c = Swal.getContainer(); if (c) c.style.zIndex = '3000'; },
      });
    } finally {
      setUlParsing(false);
    }
  };
  const handleUlFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (f) { await ulParseFile(f); e.target.value = ''; }
  };
  const handleUlDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) await ulParseFile(f);
  };
  const handleUlUpload = async () => {
    if (!ulSelectedFile || !ulReadyRows.length) return;
    setUlUploading(true);
    try {
      // Build a new workbook from only ready rows so blocked/error rows are never sent
      const wb = XLSX.utils.book_new();
      const wsData = [
        ['Mapping ID', 'Expected Registrations', 'Expected Income', 'Actual Registrations', 'Actual Income'],
        ...ulReadyRows.map((r) => [
          r.parsed.mappingId,
          r.parsed.exReg,
          r.parsed.exInc,
          r.parsed.acReg ?? '',
          r.parsed.acInc ?? '',
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), 'Upload');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const uploadFile = new File([buf], 'upload.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const res = await uploadMonthlyTargets(uploadFile);
      // Backend row numbers: row 2 = ulReadyRows[0], row 3 = ulReadyRows[1], etc.
      const failureByBackendRow = new Map((res?.failures || []).map((f) => [f.row, f]));
      setUlRows((prev) => prev.map((row) => {
        if (row.errors.length > 0) return row; // keep blocked rows unchanged
        const readyIdx = ulReadyRows.findIndex((r) => r.rowNumber === row.rowNumber);
        if (readyIdx === -1) return row;
        const backendRow = readyIdx + 2;
        if (failureByBackendRow.has(backendRow)) {
          const f = failureByBackendRow.get(backendRow);
          return { ...row, status: 'error', message: (f?.errors || []).join(', ') || 'Failed.' };
        }
        return { ...row, status: 'success', message: 'Uploaded' };
      }));
      Swal.fire({
        icon: (res?.failures || []).length ? 'warning' : 'success',
        title: 'Upload finished',
        text: `${res?.summary?.successCount || 0} succeeded, ${res?.summary?.failureCount || 0} failed.`,
        confirmButtonColor: '#0f172a',
        didOpen: () => { const c = Swal.getContainer(); if (c) c.style.zIndex = '3000'; },
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      Swal.fire({
        title: 'Upload failed',
        text: err?.response?.data?.message || 'Unable to upload file.',
        icon: 'error',
        didOpen: () => { const c = Swal.getContainer(); if (c) c.style.zIndex = '3000'; },
      });
    } finally {
      setUlUploading(false);
    }
  };

  /* ── Add Target handlers ─────────────────────────────────── */
  const atMappingOptions = useMemo(() => {
    const staffMap = Object.fromEntries(options.staff.map((s) => [s.id, s]));
    const courseMap = Object.fromEntries(options.courseCodes.map((c) => [c.id, c]));
    const yr = query.year && query.year !== 'all' ? String(query.year) : null;
    const assignments = yr
      ? (() => { const fyKeys = getFyMonthKeys(yr); return options.assignments.filter((a) => a.monthKey && fyKeys.has(a.monthKey)); })()
      : options.assignments;
    return assignments.map((a) => {
      const staff = staffMap[a.staffId];
      const course = courseMap[a.courseCodeId];
      return {
        value: a.id,
        label: `${staff?.name || '?'} · ${course?.shortName || course?.fullName || '?'} (${a.monthLabel || a.monthKey})`,
      };
    });
  }, [options.assignments, options.staff, options.courseCodes, query.year]);

  const openAtModal = () => {
    setAtForm({ mappingId: null, exReg: '0', exInc: '0', acReg: '', acInc: '' });
    setAtErrors({});
    setAtModalOpen(true);
  };
  const closeAtModal = () => {
    if (atSubmitting) return;
    setAtModalOpen(false);
  };
  const handleAtSubmit = async () => {
    if (atSubmitting) return;
    const errs = {};
    if (!atForm.mappingId) errs.mappingId = 'Please choose a staff-course assignment.';
    if (atForm.exReg === '' || Number(atForm.exReg) < 0) errs.exReg = 'Expected registrations must be ≥ 0.';
    if (atForm.exInc === '' || Number(atForm.exInc) < 0) errs.exInc = 'Expected income must be ≥ 0.';
    if (Object.keys(errs).length) { setAtErrors(errs); return; }
    try {
      setAtSubmitting(true);
      setAtErrors({});
      await createMonthlyTarget({
        staffCourseMappingId: Number(atForm.mappingId),
        expectingRegistrations: Number(atForm.exReg),
        expectingIncome: Number(atForm.exInc),
        actualRegistrations: atForm.acReg !== '' ? Number(atForm.acReg) : null,
        actualIncome: atForm.acInc !== '' ? Number(atForm.acInc) : null,
      });
      Swal.fire({ title: 'Target added', icon: 'success', timer: 1400, showConfirmButton: false, toast: true, position: 'top-end' });
      setAtModalOpen(false);
      setReloadKey((k) => k + 1);
    } catch (err) {
      const apiErrs = err?.response?.data?.errors || {};
      if (Object.keys(apiErrs).length) setAtErrors(apiErrs);
      Swal.fire('Failed to save', err?.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setAtSubmitting(false);
    }
  };

  /* ── Course Detail Modal handlers ───────────────────────── */
  const openCdModal = (staff, course) => {
    setCdModal({ staff, course });
    setCdChangeStaff(false);
    setCdNewStaffId(null);
    setCdEditMonth(null);
    setCdSelectedMonths(new Set());
    setCdBulkTransferStaffId(null);
    setCdSaving(false);
  };

  const closeCdModal = () => {
    if (cdSaving) return;
    setCdModal(null);
    setCdChangeStaff(false);
    setCdNewStaffId(null);
    setCdEditMonth(null);
    setCdSelectedMonths(new Set());
    setCdBulkTransferStaffId(null);
  };

  const handleCdBulkTransfer = async () => {
    if (!cdBulkTransferStaffId || cdSelectedMonths.size === 0 || !cdModal) return;
    const newStaffOption = modalStaffOptions.find((o) => String(o.value) === String(cdBulkTransferStaffId));
    const monthLabels = Array.from(cdSelectedMonths).map((num) => {
      const mItem = REPORT_MONTHS.find((mi) => mi.num === num);
      const yr = mItem?.nextYr ? String(Number(query.year) + 1) : query.year;
      return `${mItem?.name} '${String(yr).slice(-2)}`;
    }).join(', ');
    const confirmed = await Swal.fire({
      title: 'Transfer selected months?',
      html: `Assign <strong>${monthLabels}</strong> for <strong>${cdModal.course.shortName}</strong> to <strong>${newStaffOption?.meta?.name || newStaffOption?.label || 'selected staff'}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      confirmButtonText: 'Yes, transfer',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '3000';
      },
    });
    if (!confirmed.isConfirmed) return;
    try {
      setCdSaving(true);
      const failures = [];
      for (const num of cdSelectedMonths) {
        const mItem = REPORT_MONTHS.find((mi) => mi.num === num);
        const yr = mItem?.nextYr ? String(Number(query.year) + 1) : query.year;
        const monthKey = `${yr}-${num}`;
        const d = cdModal.course.months[num];
        try {
          if (d?.mappingId) {
            await updateStaffCourseMapping(d.mappingId, {
              staffId: Number(cdBulkTransferStaffId),
              courseCodeId: cdModal.course.id,
              monthKey,
            });
          } else {
            await createStaffCourseMapping({
              staffId: Number(cdBulkTransferStaffId),
              courseCodeId: cdModal.course.id,
              monthKey,
            });
          }
        } catch (err) {
          failures.push(`${mItem?.name}: ${err?.response?.data?.message || 'failed'}`);
        }
      }
      if (failures.length > 0) {
        Swal.fire({
          title: 'Partially transferred',
          html: `Some months could not be transferred:<br><small>${failures.join('<br>')}</small>`,
          icon: 'warning',
        });
      } else {
        Swal.fire({ title: 'Transferred', icon: 'success', timer: 1600, showConfirmButton: false });
      }
      setCdModal(null);
      setCdSelectedMonths(new Set());
      setCdBulkTransferStaffId(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not transfer months.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const handleCdChangeStaff = async () => {
    if (!cdNewStaffId || !cdModal) return;
    const newStaffOption = modalStaffOptions.find((o) => String(o.value) === String(cdNewStaffId));
    const confirmed = await Swal.fire({
      title: 'Transfer assignment?',
      html: `Move all <strong>${cdModal.course.shortName}</strong> month mappings from <strong>${cdModal.staff.name}</strong> to <strong>${newStaffOption?.meta?.name || 'the selected staff'}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      confirmButtonText: 'Yes, transfer',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '3000';
      },
    });
    if (!confirmed.isConfirmed) return;
    try {
      setCdSaving(true);
      const failures = [];
      for (const mItem of REPORT_MONTHS) {
        const monthData = cdModal.course.months[mItem.num];
        if (!monthData?.mappingId) continue;
        const yr = mItem.nextYr ? String(Number(query.year) + 1) : query.year;
        const monthKey = `${yr}-${mItem.num}`;
        try {
          await updateStaffCourseMapping(monthData.mappingId, {
            staffId: Number(cdNewStaffId),
            courseCodeId: cdModal.course.id,
            monthKey,
          });
        } catch (err) {
          failures.push(`${mItem.name}: ${err?.response?.data?.message || 'failed'}`);
        }
      }
      if (failures.length > 0) {
        Swal.fire({
          title: 'Partially transferred',
          html: `Some months could not be updated:<br><small>${failures.join('<br>')}</small>`,
          icon: 'warning',
        });
      } else {
        Swal.fire({ title: 'Transferred', icon: 'success', timer: 1600, showConfirmButton: false });
      }
      setCdModal(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not update assignments.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const handleCdRemoveAssignment = async () => {
    if (!cdModal) return;
    const confirmed = await Swal.fire({
      title: 'Remove assignment?',
      html: `Remove <strong>${cdModal.staff.name}</strong> from <strong>${cdModal.course.shortName}</strong> for all months in this financial year? Monthly targets will also be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Remove all',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '3000';
      },
    });
    if (!confirmed.isConfirmed) return;
    try {
      setCdSaving(true);
      // Delete monthly income records first (backend requires no dependents before mapping delete)
      for (const mItem of REPORT_MONTHS) {
        const monthData = cdModal.course.months[mItem.num];
        if (monthData?.monthlyIncomeId) {
          try { await deleteMonthlyTarget(monthData.monthlyIncomeId); } catch (_) { /* ignore */ }
        }
      }
      // Then delete mappings
      for (const mItem of REPORT_MONTHS) {
        const monthData = cdModal.course.months[mItem.num];
        if (monthData?.mappingId) {
          try { await deleteStaffCourseMapping(monthData.mappingId); } catch (_) { /* ignore */ }
        }
      }
      Swal.fire({ title: 'Removed', text: 'Assignment and targets removed.', icon: 'success', timer: 1600, showConfirmButton: false });
      setCdModal(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not remove assignment.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const handleCdEditMonth = (monthNum, monthData) => {
    setCdEditMonth({
      num: monthNum,
      exReg: monthData.exReg != null ? String(monthData.exReg) : '0',
      acReg: monthData.acReg != null ? String(monthData.acReg) : '',
      exInc: monthData.exInc != null ? String(monthData.exInc) : '0',
      acInc: monthData.acInc != null ? String(monthData.acInc) : '',
      mappingId: monthData.mappingId,
      monthlyIncomeId: monthData.monthlyIncomeId,
    });
  };

  const handleCdSaveMonth = async () => {
    if (!cdEditMonth || !cdModal) return;
    try {
      setCdSaving(true);
      let mappingId = cdEditMonth.mappingId;
      if (!mappingId) {
        const num = cdEditMonth.num;
        const isNextYr = ['01', '02', '03'].includes(num);
        const targetYear = isNextYr ? Number(query.year) + 1 : Number(query.year);
        const monthKey = `${targetYear}-${num}`;
        const created = await createStaffCourseMapping({
          staffId: cdModal.staff.id,
          courseCodeId: cdModal.course.id,
          monthKey,
        });
        mappingId = created?.id;
        if (!mappingId) throw new Error('Failed to get new mapping ID.');
        setCdEditMonth((prev) => ({ ...prev, mappingId }));
      }
      const payload = {
        staffCourseMappingId: mappingId,
        expectingRegistrations: cdEditMonth.exReg !== '' ? Number(cdEditMonth.exReg) : 0,
        expectingIncome: cdEditMonth.exInc !== '' ? Number(cdEditMonth.exInc) : 0,
        actualRegistrations: cdEditMonth.acReg !== '' ? Number(cdEditMonth.acReg) : null,
        actualIncome: cdEditMonth.acInc !== '' ? Number(cdEditMonth.acInc) : null,
      };
      let result;
      if (cdEditMonth.monthlyIncomeId) {
        result = await updateMonthlyTarget(cdEditMonth.monthlyIncomeId, payload);
      } else {
        result = await createMonthlyTarget(payload);
      }
      const updated = result?.data;
      const num = cdEditMonth.num;
      setCdModal((prev) => ({
        ...prev,
        course: {
          ...prev.course,
          months: {
            ...prev.course.months,
            [num]: {
              ...prev.course.months[num],
              mappingId: mappingId,
              exReg: updated?.expectingRegistrations ?? payload.expectingRegistrations,
              acReg: updated?.actualRegistrations ?? (payload.actualRegistrations ?? null),
              exInc: updated?.expectingIncome ?? payload.expectingIncome,
              acInc: updated?.actualIncome ?? (payload.actualIncome ?? null),
              monthlyIncomeId: updated?.id ?? cdEditMonth.monthlyIncomeId,
            },
          },
        },
      }));
      setCdEditMonth(null);
      setReloadKey((k) => k + 1);
      Swal.fire({ title: 'Saved', icon: 'success', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      Swal.fire('Failed to save', err?.response?.data?.message || 'Could not save targets.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const handleCdDeleteMonth = async (monthNum, monthData) => {
    if (!monthData.monthlyIncomeId) return;
    const confirmed = await Swal.fire({
      title: 'Delete targets?',
      text: 'This removes the expected and actual values for this month only.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Delete',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '3000';
      },
    });
    if (!confirmed.isConfirmed) return;
    try {
      setCdSaving(true);
      await deleteMonthlyTarget(monthData.monthlyIncomeId);
      setCdModal((prev) => ({
        ...prev,
        course: {
          ...prev.course,
          months: {
            ...prev.course.months,
            [monthNum]: {
              ...prev.course.months[monthNum],
              exReg: null,
              acReg: null,
              exInc: null,
              acInc: null,
              monthlyIncomeId: null,
            },
          },
        },
      }));
      setReloadKey((k) => k + 1);
      Swal.fire({ title: 'Deleted', icon: 'success', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not delete target.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const handleCdRemoveMonth = async (monthNum, monthData, monthLabel) => {
    if (!monthData?.mappingId) return;
    const confirmed = await Swal.fire({
      title: 'Remove staff for this month?',
      html: `Remove the assignment for <strong>${monthLabel}</strong>? The monthly target will also be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Remove',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '3000';
      },
    });
    if (!confirmed.isConfirmed) return;
    try {
      setCdSaving(true);
      if (monthData.monthlyIncomeId) {
        try { await deleteMonthlyTarget(monthData.monthlyIncomeId); } catch (_) { /* ignore */ }
      }
      await deleteStaffCourseMapping(monthData.mappingId);
      setCdModal((prev) => ({
        ...prev,
        course: {
          ...prev.course,
          months: {
            ...prev.course.months,
            [monthNum]: undefined,
          },
        },
      }));
      setReloadKey((k) => k + 1);
      Swal.fire({ title: 'Removed', icon: 'success', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not remove assignment.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  const renderSortButton = (label, key) => (    <button
      type="button"
      onClick={() => handleSort(key)}
      style={{
        border: 'none',
        background: 'transparent',
        color: query.sortBy === key ? '#1d4ed8' : '#475569',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.8px',
        cursor: 'pointer',
        textTransform: 'uppercase',
      }}
    >
      {label}
      <span style={{ marginLeft: 6, fontSize: 11 }}>
        {query.sortBy === key ? (query.sortDir === 'asc' ? '↑' : '↓') : ''}
      </span>
    </button>
  );

  const totalPages = Math.max(1, meta?.totalPages || 1);

  return (
    <AdminSalesLayout title="Coordinator Target" currentSection="staffCourseMapping">
      <PageSection>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        <SectionCard>
          <SectionTitle>Pipeline Overview</SectionTitle>
          {summaryItems.length === 0 ? (
            <EmptyState>Loading summary...</EmptyState>
          ) : (
            <SummaryGrid>
              {summaryItems.map((item) => (
                <SummaryCard key={item.label}>
                  <SummaryLabel>{item.label}</SummaryLabel>
                  <SummaryValue>{item.value ?? 0}</SummaryValue>
                  <SummaryHint>{item.hint}</SummaryHint>
                </SummaryCard>
              ))}
            </SummaryGrid>
          )}
        </SectionCard>

        <SectionCard>
          {/* ── Page header ───────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <SectionTitle style={{ margin: 0 }}>Assignments</SectionTitle>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                Manage staff-course assignments and monthly targets for the financial year.
              </div>
            </div>
            <PrimaryButton type="button" onClick={openCreateModal} style={{ flexShrink: 0 }}>
              <MdAdd size={17} />
              Assign Course Code
            </PrimaryButton>
          </div>

          {/* ── Quick-action groups ────────────────────────── */}
          <ActionGroupsBar>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {/* Directories */}
              <div>
                <ActionGroupLabel>Directories</ActionGroupLabel>
                <ActionGroupRow>
                  <IconActionBtn
                    type="button"
                    onClick={openStaffDirectory}
                    disabled={optionsLoading}
                    $borderColor="#c7d2fe" $bg="#eef2ff" $color="#4338ca"
                    $hoverBg="#e0e7ff" $hoverBorder="#818cf8" $hoverColor="#3730a3"
                    $shadow="rgba(99,102,241,0.12)"
                  >
                    <IconBubble $bg="#e0e7ff" $color="#4338ca"><MdPeople size={15} /></IconBubble>
                    Staff
                  </IconActionBtn>
                  <IconActionBtn
                    type="button"
                    onClick={openCourseDirectory}
                    disabled={optionsLoading}
                    $borderColor="#c7d2fe" $bg="#eef2ff" $color="#4338ca"
                    $hoverBg="#e0e7ff" $hoverBorder="#818cf8" $hoverColor="#3730a3"
                    $shadow="rgba(99,102,241,0.12)"
                  >
                    <IconBubble $bg="#e0e7ff" $color="#4338ca"><MdMenuBook size={15} /></IconBubble>
                    Courses
                  </IconActionBtn>
                </ActionGroupRow>
              </div>

              {/* Mapping data */}
              <div>
                <ActionGroupLabel>Mapping Data</ActionGroupLabel>
                <ActionGroupRow>
                  <IconActionBtn
                    type="button"
                    onClick={handleDownloadTemplate}
                    $borderColor="#fed7aa" $bg="#fff7ed" $color="#c2410c"
                    $hoverBg="#ffedd5" $hoverBorder="#fb923c" $hoverColor="#9a3412"
                    $shadow="rgba(234,88,12,0.10)"
                  >
                    <IconBubble $bg="#ffedd5" $color="#ea580c"><MdDescription size={15} /></IconBubble>
                    Mapping Template
                  </IconActionBtn>
                  <IconActionBtn
                    as="a"
                    href="/admin/staff-course-mapping-upload"
                    target="_blank"
                    rel="noreferrer"
                    $borderColor="#fed7aa" $bg="#fff7ed" $color="#c2410c"
                    $hoverBg="#ffedd5" $hoverBorder="#fb923c" $hoverColor="#9a3412"
                    $shadow="rgba(234,88,12,0.10)"
                  >
                    <IconBubble $bg="#ffedd5" $color="#ea580c"><MdUpload size={15} /></IconBubble>
                    Upload Mappings
                  </IconActionBtn>
                  <IconActionBtn
                    type="button"
                    onClick={handleDownloadExcel}
                    $borderColor="#fed7aa" $bg="#fff7ed" $color="#c2410c"
                    $hoverBg="#ffedd5" $hoverBorder="#fb923c" $hoverColor="#9a3412"
                    $shadow="rgba(234,88,12,0.10)"
                  >
                    <IconBubble $bg="#ffedd5" $color="#ea580c"><MdDownload size={15} /></IconBubble>
                    Export Excel
                  </IconActionBtn>
                </ActionGroupRow>
              </div>
            </div>
          </ActionGroupsBar>

          {/* ── Filters ───────────────────────────────────── */}
          <Toolbar>
            <ToolbarLeft>
              <FieldGroup>
                <FieldLabel htmlFor="yearPicker">Year</FieldLabel>
                <YearPicker
                  value={query.year !== 'all' ? query.year : null}
                  onChange={(yr) => handleYearChange(yr ? { value: yr } : null)}
                  placeholder="All years"
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel htmlFor="courseShortNameSearch">Course Short Name</FieldLabel>
                <SearchInput
                  id="courseShortNameSearch"
                  type="text"
                  placeholder="Filter by short name…"
                  value={courseShortNameSearch}
                  onChange={(e) => setCourseShortNameSearch(e.target.value)}
                />
              </FieldGroup>
            </ToolbarLeft>
            <ToolbarRight>
              <GhostButton type="button" onClick={handleClearFilters}>
                <MdRefresh size={15} />
                Reset
              </GhostButton>
            </ToolbarRight>
          </Toolbar>

          {/* Monthly Target Actions */}
          <MtStripLabel>Monthly Target Actions</MtStripLabel>
          <MtActionsStrip>
            <MtActionTile
              type="button"
              onClick={openDlModal}
              $bg="#eff6ff"
              $borderColor="#bfdbfe"
              $hoverBorder="#60a5fa"
              $shadow="rgba(37,99,235,0.12)"
            >
              <MtTileIcon $iconBg="#dbeafe" $iconColor="#1d4ed8">
                <MdDownload size={22} />
              </MtTileIcon>
              <MtTileText>
                <MtTileTitle>Download Template</MtTileTitle>
                <MtTileDesc>Get the Excel file pre-filled with assignments to enter targets offline</MtTileDesc>
              </MtTileText>
            </MtActionTile>

            <MtActionTile
              type="button"
              onClick={openUlModal}
              $bg="#f0fdf4"
              $borderColor="#bbf7d0"
              $hoverBorder="#4ade80"
              $shadow="rgba(22,163,74,0.12)"
            >
              <MtTileIcon $iconBg="#dcfce7" $iconColor="#15803d">
                <MdCloudUpload size={22} />
              </MtTileIcon>
              <MtTileText>
                <MtTileTitle>Upload Targets</MtTileTitle>
                <MtTileDesc>Import the filled template to bulk-create or update monthly targets</MtTileDesc>
              </MtTileText>
            </MtActionTile>

            <MtActionTile
              type="button"
              onClick={openAtModal}
              $bg="#faf5ff"
              $borderColor="#e9d5ff"
              $hoverBorder="#c084fc"
              $shadow="rgba(139,92,246,0.12)"
            >
              <MtTileIcon $iconBg="#f3e8ff" $iconColor="#7c3aed">
                <MdAdd size={22} />
              </MtTileIcon>
              <MtTileText>
                <MtTileTitle>Add Target</MtTileTitle>
                <MtTileDesc>Manually set a monthly target for a single staff-course assignment</MtTileDesc>
              </MtTileText>
            </MtActionTile>
          </MtActionsStrip>

          {loading || reportLoading ? (
            <EmptyState>Loading...</EmptyState>
          ) : !query.year || query.year === 'all' ? (
            <EmptyState>Select a year to view the assignment report.</EmptyState>
          ) : reportError ? (
            <EmptyState style={{ color: '#dc2626' }}>{reportError}</EmptyState>
          ) : !reportData || reportData.data.length === 0 ? (
            <EmptyState>No data found for {query.year}.</EmptyState>
          ) : (
            <ReportWrapper>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Financial Year: Apr {query.year} – Mar {Number(query.year) + 1}
                </div>
                <IconActionBtn
                  type="button"
                  onClick={handleDownloadReport}
                  $borderColor="#bfdbfe" $bg="#eff6ff" $color="#1d4ed8"
                  $hoverBg="#dbeafe" $hoverBorder="#60a5fa" $hoverColor="#1e40af"
                  $shadow="rgba(37,99,235,0.12)"
                >
                  <IconBubble $bg="#dbeafe" $color="#1d4ed8"><MdDownload size={14} /></IconBubble>
                  Download Report
                </IconActionBtn>
              </div>
              {(() => {
                const snFilter = courseShortNameSearch.trim().toLowerCase();
                const filteredReport = snFilter
                  ? reportData.data
                      .map((staff) => ({ ...staff, courses: staff.courses.filter((c) => c.shortName?.toLowerCase().includes(snFilter)) }))
                      .filter((staff) => staff.courses.length > 0)
                  : reportData.data;
                if (filteredReport.length === 0) {
                  return <EmptyState>No courses match "{courseShortNameSearch.trim()}".</EmptyState>;
                }
                return filteredReport.map((staff) => (
                <StaffBlock key={staff.id}>
                  <StaffBlockHeader>{staff.name}</StaffBlockHeader>
                  <ReportTableWrapper>
                    <ReportTable>
                      <thead>
                        <tr>
                          <RThLabel rowSpan={2}>Course Short Name</RThLabel>
                          {REPORT_MONTHS.map((m) => (
                            <RThMonth key={m.num} colSpan={6}>
                              {m.name} <span style={{ fontSize: 9, opacity: 0.75 }}>'{m.nextYr ? String(Number(query.year) + 1).slice(-2) : String(query.year).slice(-2)}</span>
                            </RThMonth>
                          ))}
                          <RThYearGroup colSpan={6}>Year Total</RThYearGroup>
                        </tr>
                        <tr>
                          {REPORT_MONTHS.flatMap((m) => [
                            <RThSub key={`${m.num}-er`}>Ex Reg</RThSub>,
                            <RThSub key={`${m.num}-ar`}>Ac Reg</RThSub>,
                            <RThGrowth key={`${m.num}-rd`}>Reg Δ</RThGrowth>,
                            <RThSub key={`${m.num}-ei`}>Ex Inc</RThSub>,
                            <RThSub key={`${m.num}-ai`}>Ac Inc</RThSub>,
                            <RThGrowth key={`${m.num}-id`}>Inc Δ</RThGrowth>,
                          ])}
                          <RThYearSub>ΣEx Reg</RThYearSub>
                          <RThYearSub>ΣAc Reg</RThYearSub>
                          <RThYearSub>Reg %</RThYearSub>
                          <RThYearSub>ΣEx Inc</RThYearSub>
                          <RThYearSub>ΣAc Inc</RThYearSub>
                          <RThYearSub>Inc %</RThYearSub>
                        </tr>
                      </thead>
                      <tbody>
                        {staff.courses.map((course, rowIdx) => {
                          const stripe = rowIdx % 2 === 1;
                          let sumExReg = 0, sumAcReg = 0, sumExInc = 0, sumAcInc = 0;
                          let hasReg = false, hasInc = false;
                          REPORT_MONTHS.forEach((m) => {
                            const d = course.months[m.num];
                            if (d?.exReg != null) { sumExReg += Number(d.exReg); hasReg = true; }
                            if (d?.acReg != null) { sumAcReg += Number(d.acReg); hasReg = true; }
                            if (d?.exInc != null) { sumExInc += Number(d.exInc); hasInc = true; }
                            if (d?.acInc != null) { sumAcInc += Number(d.acInc); hasInc = true; }
                          });
                          const regPct = sumExReg > 0 ? ((sumAcReg - sumExReg) / sumExReg * 100) : null;
                          const incPct = sumExInc > 0 ? ((sumAcInc - sumExInc) / sumExInc * 100) : null;
                          return (
                            <tr key={course.id}>
                              <RTdName
                                $stripe={stripe}
                                title={`${course.fullName} — double-click to manage`}
                                onDoubleClick={() => openCdModal(staff, course)}
                              >
                                {course.shortName}
                                <span style={{ display: 'block', fontSize: 9, color: '#94a3b8', marginTop: 1, fontWeight: 400 }}>⚙ dbl-click</span>
                              </RTdName>
                              {REPORT_MONTHS.flatMap((m) => {
                                const d = course.months[m.num];
                                const exReg = d?.exReg != null ? Number(d.exReg) : null;
                                const acReg = d?.acReg != null ? Number(d.acReg) : null;
                                const exInc = d?.exInc != null ? Number(d.exInc) : null;
                                const acInc = d?.acInc != null ? Number(d.acInc) : null;
                                const regDiff = (acReg != null && exReg != null) ? acReg - exReg : null;
                                const incDiff = (acInc != null && exInc != null) ? acInc - exInc : null;
                                const regMPct = (regDiff != null && exReg > 0) ? (regDiff / exReg * 100) : null;
                                const incMPct = (incDiff != null && exInc > 0) ? (incDiff / exInc * 100) : null;
                                const regSign = regDiff == null ? null : regDiff > 0 ? 'pos' : regDiff < 0 ? 'neg' : null;
                                const incSign = incDiff == null ? null : incDiff > 0 ? 'pos' : incDiff < 0 ? 'neg' : null;
                                return [
                                  <RTd key={`${m.num}-er`} $stripe={stripe} $empty={exReg == null}>{exReg != null ? formatReportNumber(exReg) : '—'}</RTd>,
                                  <RTd key={`${m.num}-ar`} $stripe={stripe} $empty={acReg == null}>{acReg != null ? formatReportNumber(acReg) : '—'}</RTd>,
                                  <RTdGrowth key={`${m.num}-rd`} $stripe={stripe} $sign={regSign}>
                                    {regDiff == null ? '—' : (
                                      <>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                          {regDiff > 0 ? <MdArrowUpward style={{ fontSize: 10 }} /> : regDiff < 0 ? <MdArrowDownward style={{ fontSize: 10 }} /> : null}
                                          {formatReportNumber(Math.abs(regDiff))}
                                        </div>
                                        {regMPct != null && <div style={{ fontSize: 9, opacity: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                          {regMPct > 0 ? <MdArrowUpward style={{ fontSize: 8 }} /> : regMPct < 0 ? <MdArrowDownward style={{ fontSize: 8 }} /> : null}
                                          {Math.abs(regMPct).toFixed(1)}%
                                        </div>}
                                      </>
                                    )}
                                  </RTdGrowth>,
                                  <RTd key={`${m.num}-ei`} $stripe={stripe} $empty={exInc == null}>{exInc != null ? formatReportNumber(exInc) : '—'}</RTd>,
                                  <RTd key={`${m.num}-ai`} $stripe={stripe} $empty={acInc == null}>{acInc != null ? formatReportNumber(acInc) : '—'}</RTd>,
                                  <RTdGrowth key={`${m.num}-id`} $stripe={stripe} $sign={incSign}>
                                    {incDiff == null ? '—' : (
                                      <>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                          {incDiff > 0 ? <MdArrowUpward style={{ fontSize: 10 }} /> : incDiff < 0 ? <MdArrowDownward style={{ fontSize: 10 }} /> : null}
                                          {formatReportNumber(Math.abs(incDiff))}
                                        </div>
                                        {incMPct != null && <div style={{ fontSize: 9, opacity: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                          {incMPct > 0 ? <MdArrowUpward style={{ fontSize: 8 }} /> : incMPct < 0 ? <MdArrowDownward style={{ fontSize: 8 }} /> : null}
                                          {Math.abs(incMPct).toFixed(1)}%
                                        </div>}
                                      </>
                                    )}
                                  </RTdGrowth>,
                                ];
                              })}
                              <RTdYear $stripe={stripe} $sign="neutral">{hasReg ? formatReportNumber(sumExReg) : '—'}</RTdYear>
                              <RTdYear $stripe={stripe} $sign="neutral">{hasReg ? formatReportNumber(sumAcReg) : '—'}</RTdYear>
                              <RTdYear $stripe={stripe} $sign={regPct == null ? null : regPct > 0 ? 'pos' : regPct < 0 ? 'neg' : 'neutral'}>
                                {regPct == null ? '—' : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>{regPct > 0 ? <MdArrowUpward style={{ fontSize: 10 }} /> : regPct < 0 ? <MdArrowDownward style={{ fontSize: 10 }} /> : null}{Math.abs(regPct).toFixed(1)}%</span>}
                              </RTdYear>
                              <RTdYear $stripe={stripe} $sign="neutral">{hasInc ? formatReportNumber(sumExInc) : '—'}</RTdYear>
                              <RTdYear $stripe={stripe} $sign="neutral">{hasInc ? formatReportNumber(sumAcInc) : '—'}</RTdYear>
                              <RTdYear $stripe={stripe} $sign={incPct == null ? null : incPct > 0 ? 'pos' : incPct < 0 ? 'neg' : 'neutral'}>
                                {incPct == null ? '—' : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>{incPct > 0 ? <MdArrowUpward style={{ fontSize: 10 }} /> : incPct < 0 ? <MdArrowDownward style={{ fontSize: 10 }} /> : null}{Math.abs(incPct).toFixed(1)}%</span>}
                              </RTdYear>
                            </tr>
                          );
                        })}
                      </tbody>
                    </ReportTable>
                  </ReportTableWrapper>
                </StaffBlock>
              ));
              })()}
            </ReportWrapper>
          )}
        </SectionCard>
      </PageSection>

      {staffModalOpen && (
        <ModalOverlay>
          <StaffModalCard>
            <ModalHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: 20
                }}>
                  <MdPeople />
                </div>
                <div>
                  <ModalTitle style={{ margin: 0 }}>Staff Directory</ModalTitle>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {staffDirectoryItems.length} {staffDirectoryItems.length === 1 ? 'member' : 'members'} found
                  </div>
                </div>
              </div>
              <CloseButton type="button" onClick={closeStaffDirectory}>&times;</CloseButton>
            </ModalHeader>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 200px',
              gap: 16,
              marginBottom: 20,
              paddingBottom: 20,
              borderBottom: '1px solid #e2e8f0'
            }}>
              <FieldGroup>
                <FieldLabel htmlFor="staffSearch">Search staff</FieldLabel>
                <SearchInput
                  id="staffSearch"
                  placeholder="Search by name, staff ID, or email"
                  value={staffSearch}
                  onChange={(event) => setStaffSearch(event.target.value)}
                  autoFocus
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="staffStatusFilter">Login status</FieldLabel>
                <ModalSelect
                  id="staffStatusFilter"
                  value={staffStatusFilter}
                  onChange={(event) => setStaffStatusFilter(event.target.value)}
                >
                  {staffStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status === 'all' ? 'All statuses' : formatStatusLabel(status)}
                    </option>
                  ))}
                </ModalSelect>
              </FieldGroup>
            </div>

            {optionsLoading ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>⏳</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>Loading staff directory...</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Please wait while we fetch staff details</div>
              </div>
            ) : staffDirectoryItems.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>🔍</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>
                  {staffSearch.trim() || staffStatusFilter !== 'all'
                    ? 'No staff members found'
                    : 'No staff available'}
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  {staffSearch.trim() || staffStatusFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria'
                    : 'Staff details are not available at this moment'}
                </div>
              </div>
            ) : (
              <StaffGrid>
                {staffDirectoryItems.map((staff, index) => {
                  const staffKey = String(staff.id ?? staff.employeeNumber ?? `${staff.name || 'staff'}-${index}`);
                  const initials = getInitials(staff.name);
                  const imageSrc = staff.imagePath && !staffImageErrors[staffKey]
                    ? getImageURL(staff.imagePath)
                    : '';
                  return (
                    <StaffCard key={staffKey}>
                      <StaffAvatar>
                        {imageSrc ? (
                          <StaffPhoto
                            src={imageSrc}
                            alt={`${staff.name || 'Staff'} profile`}
                            onError={() => setStaffImageErrors((prev) => ({ ...prev, [staffKey]: true }))}
                          />
                        ) : (
                          <StaffInitials>{initials}</StaffInitials>
                        )}
                      </StaffAvatar>
                      <StaffDetails>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
                          <StaffNameLine title={staff.name || 'Unnamed Staff'} style={{ flex: 1, minWidth: 0 }}>
                            {staff.name || 'Unnamed Staff'}
                          </StaffNameLine>
                          {staff.name ? (
                            <CopyButton
                              type="button"
                              title="Copy staff name"
                              onClick={() => copyStaffName(staff.name)}
                            >
                              <MdContentCopy size={15} />
                            </CopyButton>
                          ) : null}
                        </div>
                        <StaffMetaLine>
                          <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>ID:</span>
                          <span style={{ fontWeight: 600, color: '#475569' }}>
                            {staff.id ?? staff.employeeNumber ?? 'N/A'}
                          </span>
                          <CopyButton
                            type="button"
                            title="Copy staff ID"
                            onClick={() => copyStaffId(staff.id ?? staff.employeeNumber)}
                          >
                            <MdContentCopy size={15} />
                          </CopyButton>
                        </StaffMetaLine>
                        <StaffMetaLine>
                          <StatusBadge $status={(staff.loginStatus || 'unknown').toLowerCase()}>
                            {formatStatusLabel(staff.loginStatus)}
                          </StatusBadge>
                        </StaffMetaLine>
                        {staff.email && (
                          <StaffMetaLine style={{ fontSize: 12, marginTop: 2 }}>
                            <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={staff.email}>
                              {staff.email}
                            </span>
                          </StaffMetaLine>
                        )}
                        {(staff.position || staff.type) && (
                          <StaffTagsRow>
                            {staff.position && <StaffTag>{staff.position}</StaffTag>}
                            {staff.type && <StaffTag>{staff.type}</StaffTag>}
                          </StaffTagsRow>
                        )}
                      </StaffDetails>
                    </StaffCard>
                  );
                })}
              </StaffGrid>
            )}
          </StaffModalCard>
        </ModalOverlay>
      )}

      {courseModalOpen && (
        <ModalOverlay>
          <StaffModalCard>
            <ModalHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: 20
                }}>
                  <MdMenuBook />
                </div>
                <div>
                  <ModalTitle style={{ margin: 0 }}>Course Codes</ModalTitle>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {courseDirectoryItems.length} {courseDirectoryItems.length === 1 ? 'course' : 'courses'} found
                  </div>
                </div>
              </div>
              <CloseButton type="button" onClick={closeCourseDirectory}>&times;</CloseButton>
            </ModalHeader>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 16,
              marginBottom: 20,
              paddingBottom: 20,
              borderBottom: '1px solid #e2e8f0'
            }}>
              <FieldGroup>
                <FieldLabel htmlFor="courseSearch">Search courses</FieldLabel>
                <SearchInput
                  id="courseSearch"
                  placeholder="Search by code or name"
                  value={courseSearch}
                  onChange={(event) => setCourseSearch(event.target.value)}
                  autoFocus
                />
              </FieldGroup>
            </div>

            {optionsLoading ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>⏳</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>Loading course codes...</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Please wait while we fetch course details</div>
              </div>
            ) : courseDirectoryItems.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                color: '#64748b'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>🔍</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>
                  {courseSearch.trim()
                    ? 'No courses found'
                    : 'No courses available'}
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  {courseSearch.trim()
                    ? 'Try adjusting your search query'
                    : 'Course code details are not available at this moment'}
                </div>
              </div>
            ) : (
              <CourseGrid>
                {courseDirectoryItems.map((course, index) => {
                  const courseKey = String(course.id ?? `${course.shortName || course.fullName || 'course'}-${index}`);
                  return (
                    <CourseCard key={courseKey}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Tag>{course.shortName || course.code || '—'}</Tag>
                          {course.shortName ? (
                            <CopyButton
                              type="button"
                              title="Copy course short name"
                              onClick={() => copyCourseShortName(course.shortName)}
                              style={{ padding: 2 }}
                            >
                              <MdContentCopy size={15} />
                            </CopyButton>
                          ) : null}
                        </div>
                        <CourseTitle title={course.fullName || course.shortName || 'Untitled course'}>
                          {course.fullName || course.shortName || 'Untitled course'}
                        </CourseTitle>
                      </div>
                      {course.shortName && course.fullName && (
                        <CourseMeta>{course.shortName} — {course.fullName}</CourseMeta>
                      )}
                      <CourseMeta>
                        <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginRight: 6 }}>ID:</span>
                        <span style={{ fontWeight: 600, color: '#475569' }}>{course.id ?? 'N/A'}</span>
                        <CopyButton
                          type="button"
                          title="Copy course ID"
                          onClick={() => copyCourseId(course.id)}
                          style={{ marginLeft: 4 }}
                        >
                          <MdContentCopy size={15} />
                        </CopyButton>
                      </CourseMeta>
                    </CourseCard>
                  );
                })}
              </CourseGrid>
            )}
          </StaffModalCard>
        </ModalOverlay>
      )}

      {modalOpen && (
        <ModalOverlay>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>{activeRecord ? 'Edit Assignment' : 'Assign Course Code'}</ModalTitle>
              <CloseButton type="button" onClick={closeModal}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleFormSubmit}>
              <FieldGroup style={{ marginBottom: 16 }}>
                <FieldLabel>Staff member</FieldLabel>
                <Select
                  value={modalStaffValue}
                  onChange={(option) => setFormData((prev) => ({ ...prev, staffId: option ? option.value : null }))}
                  options={modalStaffOptions}
                  placeholder="Select staff"
                  styles={selectStyles}
                />
                {formErrors.staffId && <FieldError>{formErrors.staffId}</FieldError>}
              </FieldGroup>

              <FieldGroup style={{ marginBottom: 16 }}>
                <FieldLabel>Course code</FieldLabel>
                <Select
                  value={modalCourseValue}
                  onChange={(option) => setFormData((prev) => ({ ...prev, courseCodeId: option ? option.value : null }))}
                  options={modalCourseOptionsFiltered}
                  placeholder="Select course code"
                  styles={selectStyles}
                />
                {formErrors.courseCodeId && <FieldError>{formErrors.courseCodeId}</FieldError>}
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={showAllCourses}
                    onChange={(e) => setShowAllCourses(e.target.checked)}
                    style={{ width: 14, height: 14, accentColor: '#4f46e5', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Show full list
                    {!showAllCourses && query.year && query.year !== 'all' && assignedCourseIdsInYear.size > 0 && (
                      <span style={{ marginLeft: 5, color: '#94a3b8' }}>({assignedCourseIdsInYear.size} already assigned in {query.year} hidden)</span>
                    )}
                  </span>
                </label>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="monthKey">Assignment month</FieldLabel>
                <SearchInput
                  id="monthKey"
                  type="month"
                  value={formData.monthKey}
                  onChange={(event) => setFormData((prev) => ({ ...prev, monthKey: event.target.value }))}
                />
                {formErrors.monthKey && <FieldError>{formErrors.monthKey}</FieldError>}
              </FieldGroup>

              <ModalActions>
                <SecondaryButton type="button" onClick={closeModal} disabled={submitting}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save assignment'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* ── Course Detail / Manage Modal ────────────────────── */}
      {cdModal && (
        <ModalOverlay>
          <CourseManageCard>
            {/* Header */}
            <CmHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag style={{ fontSize: 13, padding: '4px 12px' }}>{cdModal.course.shortName}</Tag>
                  <CmTitle>{cdModal.course.fullName}</CmTitle>
                </div>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  Financial Year: Apr {query.year} – Mar {Number(query.year) + 1}
                </span>
              </div>
              <CloseButton type="button" onClick={closeCdModal} disabled={cdSaving}>&times;</CloseButton>
            </CmHeader>

            <CmBody>
              {/* Monthly Targets Section */}
              <CmSection>
                <CmSectionTitle>Monthly Targets</CmSectionTitle>

                {/* Bulk Transfer Bar */}
                {cdSelectedMonths.size > 0 && (
                  <CmBulkBar>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', whiteSpace: 'nowrap' }}>
                      {cdSelectedMonths.size} month{cdSelectedMonths.size > 1 ? 's' : ''} selected
                    </span>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <Select
                        value={modalStaffOptions.find((o) => String(o.value) === String(cdBulkTransferStaffId)) || null}
                        onChange={(opt) => setCdBulkTransferStaffId(opt ? opt.value : null)}
                        options={modalStaffOptions.filter((o) => String(o.value) !== String(cdModal.staff.id))}
                        placeholder="Assign selected months to…"
                        styles={selectStyles}
                        menuPosition="fixed"
                      />
                    </div>
                    <CmActBtnPrimary
                      onClick={handleCdBulkTransfer}
                      disabled={!cdBulkTransferStaffId || cdSaving}
                      style={{ padding: '6px 14px', fontSize: 12 }}
                    >
                      <MdSyncAlt size={13} /> {cdSaving ? '…' : 'Transfer'}
                    </CmActBtnPrimary>
                    <CmActBtn
                      onClick={() => { setCdSelectedMonths(new Set()); setCdBulkTransferStaffId(null); }}
                      disabled={cdSaving}
                      style={{ padding: '6px 12px', fontSize: 12 }}
                    >
                      Clear
                    </CmActBtn>
                  </CmBulkBar>
                )}

                <CmMonthTableWrapper>
                  <CmMonthTable>
                    <thead>
                      <tr>
                        <CmTh style={{ width: 32, textAlign: 'center', paddingLeft: 10 }}>
                          <input
                            type="checkbox"
                            disabled={!!cdEditMonth || cdSaving}
                            checked={cdSelectedMonths.size === REPORT_MONTHS.length}
                            ref={(el) => {
                              if (el) el.indeterminate = cdSelectedMonths.size > 0 && cdSelectedMonths.size < REPORT_MONTHS.length;
                            }}
                            onChange={(e) => {
                              setCdSelectedMonths(e.target.checked ? new Set(REPORT_MONTHS.map((mi) => mi.num)) : new Set());
                            }}
                            style={{ cursor: 'pointer', width: 14, height: 14 }}
                          />
                        </CmTh>
                        <CmTh style={{ width: 36, textAlign: 'center' }}>No.</CmTh>
                        <CmTh>Month</CmTh>
                        <CmTh>Ex. Reg</CmTh>
                        <CmTh>Ac. Reg</CmTh>
                        <CmTh>Ex. Income</CmTh>
                        <CmTh>Ac. Income</CmTh>
                        <CmTh>Actions</CmTh>
                      </tr>
                    </thead>
                    <tbody>
                      {REPORT_MONTHS.map((m, idx) => {
                        const d = cdModal.course.months[m.num];
                        const yr = m.nextYr ? String(Number(query.year) + 1) : query.year;
                        const monthLabel = `${m.name} '${String(yr).slice(-2)}`;
                        const isChecked = cdSelectedMonths.has(m.num);
                        const isEditing = cdEditMonth?.num === m.num;
                        const rowBg = isChecked ? { background: '#eff6ff' } : {};

                        const checkboxCell = (
                          <CmTd style={{ textAlign: 'center', paddingLeft: 10 }}>
                            <input
                              type="checkbox"
                              disabled={isEditing || cdSaving}
                              checked={isChecked}
                              onChange={() => {
                                setCdSelectedMonths((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(m.num)) next.delete(m.num); else next.add(m.num);
                                  return next;
                                });
                              }}
                              style={{ cursor: isEditing ? 'not-allowed' : 'pointer', width: 14, height: 14 }}
                            />
                          </CmTd>
                        );

                        if (!d) {
                          if (isEditing) {
                            return (
                              <CmTr key={m.num} style={{ background: '#f0fdf4' }}>
                                {checkboxCell}
                                <CmTd style={{ textAlign: 'center', color: '#64748b', fontSize: 12 }}>{idx + 1}</CmTd>
                                <CmTd><span style={{ fontWeight: 600, fontSize: 12 }}>{monthLabel}</span></CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    value={cdEditMonth.exReg}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, exReg: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={cdEditMonth.acReg}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, acReg: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    value={cdEditMonth.exInc}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, exInc: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={cdEditMonth.acInc}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, acInc: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                    <CmActBtnPrimary onClick={handleCdSaveMonth} disabled={cdSaving}>
                                      <MdSave size={12} /> {cdSaving ? '…' : 'Save'}
                                    </CmActBtnPrimary>
                                    <CmActBtn onClick={() => setCdEditMonth(null)} disabled={cdSaving}>
                                      Cancel
                                    </CmActBtn>
                                  </div>
                                </CmTd>
                              </CmTr>
                            );
                          }
                          return (
                            <CmTr key={m.num} style={rowBg}>
                              {checkboxCell}
                              <CmTd style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{idx + 1}</CmTd>
                              <CmTd><span style={{ fontWeight: 600, fontSize: 12 }}>{monthLabel}</span></CmTd>
                              <CmTd style={{ color: '#cbd5e1' }}>—</CmTd>
                              <CmTd style={{ color: '#cbd5e1' }}>—</CmTd>
                              <CmTd style={{ color: '#cbd5e1' }}>—</CmTd>
                              <CmTd style={{ color: '#cbd5e1' }}>—</CmTd>
                              <CmTd>
                                <CmActBtnPrimary
                                  onClick={() => handleCdEditMonth(m.num, { exReg: null, acReg: null, exInc: null, acInc: null, mappingId: null, monthlyIncomeId: null })}
                                  disabled={cdSaving}
                                  title="Create assignment and set targets for this month"
                                >
                                  <MdEdit size={12} /> Set
                                </CmActBtnPrimary>
                              </CmTd>
                            </CmTr>
                          );
                        }

                        const hasTarget = Boolean(d.monthlyIncomeId);
                        return (
                          <CmTr key={m.num} style={rowBg}>
                            {checkboxCell}
                            <CmTd style={{ textAlign: 'center', color: '#64748b', fontSize: 12 }}>{idx + 1}</CmTd>
                            <CmTd>
                              <span style={{ fontWeight: 600, fontSize: 12 }}>{monthLabel}</span>
                            </CmTd>
                            {isEditing ? (
                              <>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    value={cdEditMonth.exReg}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, exReg: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={cdEditMonth.acReg}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, acReg: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    value={cdEditMonth.exInc}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, exInc: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <CmInput
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={cdEditMonth.acInc}
                                    onChange={(e) => setCdEditMonth((p) => ({ ...p, acInc: e.target.value }))}
                                  />
                                </CmTd>
                                <CmTd>
                                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                    <CmActBtnPrimary onClick={handleCdSaveMonth} disabled={cdSaving}>
                                      <MdSave size={12} /> {cdSaving ? '…' : 'Save'}
                                    </CmActBtnPrimary>
                                    <CmActBtn onClick={() => setCdEditMonth(null)} disabled={cdSaving}>
                                      Cancel
                                    </CmActBtn>
                                  </div>
                                </CmTd>
                              </>
                            ) : (
                              <>
                                <CmTd style={{ color: d.exReg != null ? '#0f172a' : '#cbd5e1' }}>
                                  {d.exReg != null ? d.exReg.toLocaleString() : '—'}
                                </CmTd>
                                <CmTd style={{ color: d.acReg != null ? '#0f172a' : '#cbd5e1' }}>
                                  {d.acReg != null ? d.acReg.toLocaleString() : '—'}
                                </CmTd>
                                <CmTd style={{ color: d.exInc != null ? '#0f172a' : '#cbd5e1' }}>
                                  {d.exInc != null ? d.exInc.toLocaleString() : '—'}
                                </CmTd>
                                <CmTd style={{ color: d.acInc != null ? '#0f172a' : '#cbd5e1' }}>
                                  {d.acInc != null ? d.acInc.toLocaleString() : '—'}
                                </CmTd>
                                <CmTd>
                                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                    <CmActBtnPrimary
                                      onClick={() => handleCdEditMonth(m.num, d)}
                                      disabled={cdSaving}
                                      title={hasTarget ? 'Edit targets' : 'Set targets for this month'}
                                    >
                                      <MdEdit size={12} /> {hasTarget ? 'Edit' : 'Set'}
                                    </CmActBtnPrimary>
                                    {hasTarget && (
                                      <CmActBtnDanger
                                        onClick={() => handleCdDeleteMonth(m.num, d)}
                                        disabled={cdSaving}
                                        title="Delete targets for this month"
                                      >
                                        <MdDeleteOutline size={12} />
                                      </CmActBtnDanger>
                                    )}
                                    <CmActBtnDanger
                                      onClick={() => handleCdRemoveMonth(m.num, d, monthLabel)}
                                      disabled={cdSaving}
                                      title="Remove staff assignment for this month"
                                    >
                                      <MdPersonOff size={12} />
                                    </CmActBtnDanger>
                                  </div>
                                </CmTd>
                              </>
                            )}
                          </CmTr>
                        );
                      })}
                    </tbody>
                  </CmMonthTable>
                </CmMonthTableWrapper>
              </CmSection>
            </CmBody>
          </CourseManageCard>
        </ModalOverlay>
      )}

      {/* ── Download Monthly Target Template Modal ──────────── */}
      {dlModalOpen && (
        <ModalOverlay style={{ zIndex: 1800 }}>
          <WideModalCard>
            <ModalHeader>
              <ModalTitle>Download Monthly Target Template</ModalTitle>
              <CloseButton onClick={closeDlModal}>&times;</CloseButton>
            </ModalHeader>
            <ModalGrid>
              <FieldGroup>
                <FieldLabel htmlFor="dl-start">Start Month</FieldLabel>
                <TextInput
                  id="dl-start"
                  type="month"
                  value={dlForm.startMonth}
                  onChange={(e) => setDlForm((p) => ({ ...p, startMonth: e.target.value }))}
                />
                {dlErrors.startMonth && <FieldError>{dlErrors.startMonth}</FieldError>}
              </FieldGroup>
              <FieldGroup>
                <FieldLabel htmlFor="dl-end">End Month</FieldLabel>
                <TextInput
                  id="dl-end"
                  type="month"
                  value={dlForm.endMonth}
                  onChange={(e) => setDlForm((p) => ({ ...p, endMonth: e.target.value }))}
                />
                {dlErrors.endMonth && <FieldError>{dlErrors.endMonth}</FieldError>}
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Staff (optional)</FieldLabel>
                <Select
                  isClearable
                  placeholder="All staff"
                  value={dlForm.staffId === 'all' ? null : (options.staff.map((s) => ({ value: s.id, label: s.name })).find((o) => String(o.value) === String(dlForm.staffId)) || null)}
                  onChange={(opt) => setDlForm((p) => ({ ...p, staffId: opt ? opt.value : 'all' }))}
                  options={options.staff.map((s) => ({ value: s.id, label: s.name }))}
                  styles={selectStyles}
                  isDisabled={optionsLoading}
                />
                <MetaText>Leave empty to include all staff.</MetaText>
              </FieldGroup>
            </ModalGrid>
            <MetaText style={{ marginBottom: 12 }}>
              The template includes all staff-course assignments in the selected range with any existing target data pre-filled.
            </MetaText>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <PrimaryButton onClick={handleDlPreview} disabled={dlPreviewLoading || dlLoading}>
                {dlPreviewLoading ? 'Loading preview…' : 'Preview rows'}
              </PrimaryButton>
              <SecondaryButton onClick={handleDlDownload} disabled={dlLoading || dlPreviewLoading}>
                {dlLoading ? 'Downloading…' : 'Download .xlsx'}
              </SecondaryButton>
              <GhostButton onClick={closeDlModal} disabled={dlLoading || dlPreviewLoading}>
                Close
              </GhostButton>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              {dlPreviewLoading && <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>Loading preview…</div>}
              {!dlPreviewLoading && dlPreviewError && (
                <div style={{ padding: 16, color: '#dc2626', fontSize: 13 }}>{dlPreviewError}</div>
              )}
              {!dlPreviewLoading && !dlPreviewError && dlPreviewRows.length === 0 && (
                <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>Click "Preview rows" to see what will be included in the template.</div>
              )}
              {!dlPreviewLoading && !dlPreviewError && dlPreviewRows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                      {['Staff', 'Course', 'Period', 'Ex. Reg', 'Ex. Income', 'Ac. Reg', 'Ac. Income'].map((h) => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dlPreviewRows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '7px 12px' }}>{row.staffName}</td>
                        <td style={{ padding: '7px 12px' }}><Tag>{row.courseShortName}</Tag></td>
                        <td style={{ padding: '7px 12px' }}>{row.period}</td>
                        <td style={{ padding: '7px 12px', color: '#64748b' }}>{row.expectingRegistrations === '' ? '—' : row.expectingRegistrations}</td>
                        <td style={{ padding: '7px 12px', color: '#64748b' }}>{row.expectingIncome === '' ? '—' : row.expectingIncome}</td>
                        <td style={{ padding: '7px 12px', color: '#64748b' }}>{row.actualRegistrations === '' ? '—' : row.actualRegistrations}</td>
                        <td style={{ padding: '7px 12px', color: '#64748b' }}>{row.actualIncome === '' ? '—' : row.actualIncome}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </WideModalCard>
        </ModalOverlay>
      )}

      {/* ── Upload Monthly Target Template Modal ─────────────── */}
      {ulModalOpen && (
        <ModalOverlay style={{ zIndex: 1800 }}>
          <WideModalCard>
            <ModalHeader>
              <ModalTitle>Upload Monthly Target Template</ModalTitle>
              <CloseButton onClick={closeUlModal}>&times;</CloseButton>
            </ModalHeader>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Ready rows', val: ulReadyRows.length, color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: 'Issues', val: ulBlockedRows.length, color: '#b91c1c', bg: '#fff1f2', border: '#fecaca' },
                { label: 'Uploaded', val: ulSuccessCount, color: '#0f172a', bg: '#f8fafc', border: '#e2e8f0' },
                { label: 'Failed', val: ulFailedCount, color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
              ].map((c) => (
                <div key={c.label} style={{ borderRadius: 12, border: `1px solid ${c.border}`, background: c.bg, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{c.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: c.color, marginTop: 6 }}>{c.val}</div>
                </div>
              ))}
            </div>
            <DropZone
              onClick={() => ulFileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleUlDrop}
            >
              <input ref={ulFileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleUlFileChange} />
              <MdCloudUpload size={48} style={{ color: '#6366f1', marginBottom: 8 }} />
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                {ulParsing ? 'Analyzing file…' : 'Drop the filled template here or click to browse'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Accepts .xlsx, .xls, .csv — must be from the Monthly Target Template.
              </div>
              {ulFileMeta && (
                <div style={{ marginTop: 10, fontSize: 11, background: '#f1f5f9', borderRadius: 20, padding: '4px 14px', color: '#475569', display: 'inline-block' }}>
                  {ulFileMeta.name} · Sheet: {ulFileMeta.sheet} · {ulFileMeta.total} rows
                </div>
              )}
            </DropZone>
            {ulRows.length > 0 && ulBlockedRows.length > 0 && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  <strong>{ulBlockedRows.length} row{ulBlockedRows.length > 1 ? 's' : ''} have validation issues</strong> and will be skipped.
                  Only the {ulReadyRows.length} ready row{ulReadyRows.length !== 1 ? 's' : ''} will be uploaded.
                </div>
              </div>
            )}
            {ulRows.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                    {ulRows.length} rows · review and upload
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <PrimaryButton onClick={handleUlUpload} disabled={ulUploading || !ulReadyRows.length}>
                      {ulUploading ? 'Uploading…' : `Upload ${ulReadyRows.length} rows`}
                    </PrimaryButton>
                    <GhostButton onClick={ulResetWorkspace} disabled={ulUploading}>
                      Clear
                    </GhostButton>
                  </div>
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                        {['Row', 'Mapping ID', 'Ex. Reg', 'Ex. Income', 'Ac. Reg', 'Ac. Income', 'Status'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#475569', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ulRows.map((row) => (
                        <tr key={row.rowNumber} style={{ borderBottom: '1px solid #f1f5f9', background: row.errors.length ? '#fff1f2' : row.status === 'success' ? '#f0fdf4' : 'transparent' }}>
                          <td style={{ padding: '6px 10px', color: '#64748b' }}>{row.rowNumber}</td>
                          <td style={{ padding: '6px 10px' }}>{row.preview.mappingId}</td>
                          <td style={{ padding: '6px 10px' }}>{row.preview.expectedRegistrations || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{row.preview.expectedIncome || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{row.preview.actualRegistrations || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{row.preview.actualIncome || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>
                            {row.errors.length > 0 ? (
                              <div>
                                <UlBadge $error>Issues</UlBadge>
                                {row.errors.map((e, i) => <div key={i} style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{e}</div>)}
                              </div>
                            ) : row.status === 'success' ? (
                              <UlBadge $success>Uploaded</UlBadge>
                            ) : row.status === 'error' ? (
                              <div>
                                <UlBadge $error>Failed</UlBadge>
                                {row.message && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{row.message}</div>}
                              </div>
                            ) : (
                              <UlBadge>Ready</UlBadge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <GhostButton onClick={closeUlModal} disabled={ulUploading}>Close</GhostButton>
            </div>
          </WideModalCard>
        </ModalOverlay>
      )}

      {/* ── Add Target Modal ─────────────────────────────────── */}
      {atModalOpen && (
        <ModalOverlay style={{ zIndex: 1800 }}>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>Add Monthly Target</ModalTitle>
              <CloseButton onClick={closeAtModal}>&times;</CloseButton>
            </ModalHeader>
            <FieldGroup style={{ marginBottom: 14 }}>
              <FieldLabel>Staff-Course Assignment</FieldLabel>
              <Select
                placeholder={optionsLoading ? 'Loading…' : 'Select assignment…'}
                value={atMappingOptions.find((o) => String(o.value) === String(atForm.mappingId)) || null}
                onChange={(opt) => setAtForm((p) => ({ ...p, mappingId: opt ? opt.value : null }))}
                options={atMappingOptions}
                styles={{ ...selectStyles, menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
                isDisabled={optionsLoading}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {atErrors.mappingId && <FieldError>{atErrors.mappingId}</FieldError>}
            </FieldGroup>
            <ModalGrid>
              <FieldGroup>
                <FieldLabel>Expected Registrations</FieldLabel>
                <TextInput type="number" min="0" value={atForm.exReg} onChange={(e) => setAtForm((p) => ({ ...p, exReg: e.target.value }))} />
                {atErrors.exReg && <FieldError>{atErrors.exReg}</FieldError>}
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Expected Income</FieldLabel>
                <TextInput type="number" min="0" value={atForm.exInc} onChange={(e) => setAtForm((p) => ({ ...p, exInc: e.target.value }))} />
                {atErrors.exInc && <FieldError>{atErrors.exInc}</FieldError>}
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Actual Registrations <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></FieldLabel>
                <TextInput type="number" min="0" placeholder="—" value={atForm.acReg} onChange={(e) => setAtForm((p) => ({ ...p, acReg: e.target.value }))} />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Actual Income <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></FieldLabel>
                <TextInput type="number" min="0" placeholder="—" value={atForm.acInc} onChange={(e) => setAtForm((p) => ({ ...p, acInc: e.target.value }))} />
              </FieldGroup>
            </ModalGrid>
            <ModalActions>
              <GhostButton onClick={closeAtModal} disabled={atSubmitting}>Cancel</GhostButton>
              <PrimaryButton onClick={handleAtSubmit} disabled={atSubmitting}>
                {atSubmitting ? 'Saving…' : 'Add Target'}
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

    </AdminSalesLayout>
  );
}